/**
 * Reseller customer portal — Pass D step 9.
 *
 * Single-route SPA mounted at /r/:resellerSlug/customer/* (and the
 * default fallback /customer/*). Three views:
 *
 *   1. Login   — password OR request-code -> enter-code
 *   2. Trips   — list of pending / in-progress / completed trips
 *   3. Trip detail — hotel + each round status + action items
 *
 * Uses a public axios (no auth header) but stores a session token in
 * localStorage and sends it as Authorization: Bearer for trips/me.
 *
 * Magic links from confirmation emails land at:
 *   /r/{slug}/customer/magic?bookingId=123&token=abc...
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom'
import axios from 'axios'
import { displayPhone } from '../../utils/phone'

// VITE_API_ORIGIN: required on prod where the SPA host doesn't proxy /api/*.
const _apiOrigin = import.meta.env.VITE_API_ORIGIN || window.location.origin
const API = axios.create({ baseURL: `${_apiOrigin}/api/public/customer-portal`, validateStatus: () => true })

// The redemption (book-a-round) flow is live (L3).
const REDEEM_ENABLED = true

// Package hero images come back as site-relative paths (/site-assets/…).
// Absolute URLs pass through; relative ones get prefixed with the API origin.
function resolveAssetUrl(u) {
  if (!u) return null
  return /^https?:\/\//.test(u) ? u : `${_apiOrigin}${u}`
}

function authHeaders() {
  const t = localStorage.getItem('rcp_session')
  return t ? { Authorization: 'Bearer ' + t } : {}
}

// Post-login landing: ask the API which section is most relevant (trips vs
// golf passes) and navigate there. Falls back to trips on any error.
async function navigateHome(navigate, scopedPath) {
  let section = 'trips'
  try {
    const r = await API.get('/home', { headers: authHeaders() })
    if (r.status < 400 && r.data?.section) section = r.data.section
  } catch { /* fall back to trips */ }
  navigate(scopedPath(section))
}

// Brand profile is small + cacheable for the session — once fetched it
// never changes during a visit. Module-level cache. Keyed by slug, with
// a parallel host->slug map so host-mode lookups stay fast after first hit.
const _brandCache = new Map()        // slug -> brand
const _hostToSlug = new Map()        // hostname -> slug

/**
 * useResellerBrand
 *
 * Two modes:
 *   - Slug mode: route matched /r/:resellerSlug/... — fetch brand by slug.
 *   - Host mode: route is bare /customer/... — look up by current hostname
 *     and the response tells us the slug. Used when the customer portal
 *     loads on a reseller's custom domain (Phase 8).
 *
 * Both modes populate the slug cache so subsequent navigations share data.
 */
function useResellerBrand() {
  const { resellerSlug } = useParams()
  const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
  const knownHostSlug = resellerSlug || _hostToSlug.get(hostname) || null
  const [brand, setBrand] = useState(() => knownHostSlug ? _brandCache.get(knownHostSlug) || null : null)

  useEffect(() => {
    // Slug mode
    if (resellerSlug && resellerSlug !== '_') {
      if (_brandCache.has(resellerSlug)) { setBrand(_brandCache.get(resellerSlug)); return }
      API.get('/brand/' + encodeURIComponent(resellerSlug)).then(r => {
        if (r.status >= 400) return
        _brandCache.set(resellerSlug, r.data.brand)
        setBrand(r.data.brand)
      })
      return
    }
    // Host mode — only for bare routes (slug is undefined/wildcard)
    const cachedSlug = _hostToSlug.get(hostname)
    if (cachedSlug && _brandCache.has(cachedSlug)) { setBrand(_brandCache.get(cachedSlug)); return }
    API.get('/brand-by-host?host=' + encodeURIComponent(hostname)).then(r => {
      if (r.status >= 400) return
      _hostToSlug.set(hostname, r.data.brand.slug)
      _brandCache.set(r.data.brand.slug, r.data.brand)
      setBrand(r.data.brand)
    })
  }, [resellerSlug, hostname])

  return brand
}

// True when the current route doesn't carry a /r/:slug/ prefix. Driven
// by useParams so callers re-evaluate when routes change.
function useIsCustomDomainMode() {
  const { resellerSlug } = useParams()
  return !resellerSlug
}

// Build a URL within the customer portal's reseller-scoped namespace.
//
// Two output forms depending on how the current route was entered:
//   - Slug mode (route had /r/:resellerSlug/) -> /r/{slug}/customer/{rel}
//   - Host mode (route is bare /customer/...)  -> /customer/{rel}
//
// Host mode is used on a reseller's custom domain — the hostname already
// scopes the request to a reseller, so keeping the slug out of the URL
// gives the customer a clean, branded address bar.
function useScopedPath() {
  const { resellerSlug } = useParams()
  return (rel) => {
    const trimmed = String(rel || '').replace(/^\/+/, '')
    if (resellerSlug && resellerSlug !== '_') return `/r/${resellerSlug}/customer/${trimmed}`
    // Host mode: bare paths, EXCEPT 'login' which collides with the
    // Stay & Play customer login. Emit /customer/sign-in instead.
    if (trimmed === 'login') return '/customer/sign-in'
    return `/customer/${trimmed}`
  }
}

// ─── Routes (called from App.jsx) ───────────────────────────────────────

export function CustomerMagicLanding() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const scopedPath = useScopedPath()
  const [err, setErr] = useState(null)
  const [needPassword, setNeedPassword] = useState(false)
  const [pwd, setPwd] = useState('')
  const [pwd2, setPwd2] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Optional post-auth destination carried by the email link. Whitelisted
  // to trip pages only so a crafted ?next= can't bounce the customer
  // somewhere unexpected. Defaults to the trips list.
  const rawNext = params.get('next') || ''
  const hasExplicitNext = /^trips\/\d+(\/review)?$/.test(rawNext)
  // An explicit trip deep-link (from a trip email) wins; otherwise fall back
  // to the smart trips-vs-passes landing.
  function goAfterAuth() {
    if (hasExplicitNext) navigate(scopedPath(rawNext))
    else navigateHome(navigate, scopedPath)
  }

  // Guard against the magic POST firing twice — StrictMode double-invokes
  // effects in dev, and this call is NOT idempotent (it lazy-creates the
  // account). Firing once keeps dev behaviour identical to prod.
  const firedRef = useRef(false)
  useEffect(() => {
    if (firedRef.current) return
    firedRef.current = true
    const bookingId = params.get('bookingId')
    const token = params.get('token')
    if (!bookingId || !token) { setErr('Invalid link'); return }
    API.post('/sessions/magic', { token, bookingId })
      .then(r => {
        if (r.status >= 400) { setErr(r.data?.message || 'Invalid link'); return }
        localStorage.setItem('rcp_session', r.data.sessionToken)
        if (r.data.needPassword) setNeedPassword(true)
        else goAfterAuth()
      })
      .catch(e => setErr(e.message))
  }, [params, navigate])

  async function savePwd(e) {
    e.preventDefault()
    if (pwd !== pwd2) return setErr('Passwords don\'t match')
    if (pwd.length < 8) return setErr('Password must be at least 8 characters')
    setSubmitting(true); setErr(null)
    const r = await API.post('/sessions/set-password', { sessionToken: localStorage.getItem('rcp_session'), password: pwd })
    setSubmitting(false)
    if (r.status >= 400) return setErr(r.data?.message)
    goAfterAuth()
  }

  if (err) return <Shell><Box>{err}<br/><Link to={scopedPath('login')}>← Sign in</Link></Box></Shell>
  if (needPassword) return (
    <Shell>
      <h1 style={{ fontSize: 22, marginTop: 0 }}>Set a password</h1>
      <p style={{ color: '#64748b', fontSize: 14 }}>One-time setup so you can come back to manage your trip.</p>
      <form onSubmit={savePwd} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <input type="password" placeholder="Password (8+ chars)" value={pwd} onChange={e => setPwd(e.target.value)} style={inp} />
        <input type="password" placeholder="Confirm password" value={pwd2} onChange={e => setPwd2(e.target.value)} style={inp} />
        {err && <div style={errBox}>{err}</div>}
        <button type="submit" disabled={submitting} style={primaryBtn}>{submitting ? 'Saving...' : 'Save and continue'}</button>
      </form>
    </Shell>
  )
  return <Shell><Box>Loading...</Box></Shell>
}

// Bundle magic-link landing ("View My Rounds / Account" from a purchase). The
// token is a JWT minted at purchase; POST it to sign in (create account if
// needed) and drop the buyer into their full portal — no password wall.
export function CustomerBundleMagic() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const scopedPath = useScopedPath()
  const [err, setErr] = useState(null)
  const firedRef = useRef(false)
  useEffect(() => {
    if (firedRef.current) return
    firedRef.current = true
    const token = params.get('token')
    if (!token) { setErr('Invalid link'); return }
    API.post('/sessions/bundle-magic', { token })
      .then(r => {
        if (r.status >= 400) { setErr(r.data?.message || 'Invalid or expired link'); return }
        localStorage.setItem('rcp_session', r.data.sessionToken)
        navigate(scopedPath('passes'))
      })
      .catch(e => setErr(e.message))
  }, [params, navigate])
  if (err) return <Shell><Box>{err}<br /><Link to={scopedPath('login')}>← Sign in</Link></Box></Shell>
  return <Shell><Box>Signing you in…</Box></Shell>
}

export function CustomerLogin() {
  const navigate = useNavigate()
  const scopedPath = useScopedPath()
  const brand = useResellerBrand() // for reseller-branded OTP email (from + subject)
  const [step, setStep] = useState('request') // request (email -> code) | password | code | mfa
  const [email, setEmail] = useState('')
  const [pwd, setPwd] = useState('')
  const [code, setCode] = useState('')
  const [mfaCode, setMfaCode] = useState('')
  const [mfaChallenge, setMfaChallenge] = useState(null)
  const [trustDevice, setTrustDevice] = useState(true)
  const [err, setErr] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  // Common landing helper — accept the API response shape from either
  // login-password (trusted-device shortcut) or login-code, and either
  // issue the session or hand off to MFA.
  function consumeAuthResponse(data) {
    if (data.needMfa && data.mfaChallenge) {
      setMfaChallenge(data.mfaChallenge)
      setStep('mfa')
      return
    }
    localStorage.setItem('rcp_session', data.sessionToken)
    if (data.deviceToken) localStorage.setItem('rcp_device', data.deviceToken)
    navigateHome(navigate, scopedPath)
  }

  async function loginPwd(e) {
    e.preventDefault()
    setSubmitting(true); setErr(null)
    const deviceToken = localStorage.getItem('rcp_device') || undefined
    const r = await API.post('/sessions/login-password', { email, password: pwd, deviceToken })
    setSubmitting(false)
    if (r.status >= 400) return setErr(r.data?.message)
    if (r.data.needCode) {
      await API.post('/sessions/request-code', { email, resellerSlug: brand?.slug })
      setStep('code')
      return
    }
    consumeAuthResponse(r.data)
  }
  async function verifyMfa(e) {
    e.preventDefault()
    setSubmitting(true); setErr(null)
    const r = await API.post('/sessions/verify-mfa', { mfaChallenge, code: mfaCode })
    setSubmitting(false)
    if (r.status >= 400) return setErr(r.data?.message)
    localStorage.setItem('rcp_session', r.data.sessionToken)
    if (r.data.deviceToken) localStorage.setItem('rcp_device', r.data.deviceToken)
    navigateHome(navigate, scopedPath)
  }
  async function loginCode(e) {
    e.preventDefault()
    setSubmitting(true); setErr(null)
    const r = await API.post('/sessions/login-code', { email, code, deviceTrust: trustDevice })
    setSubmitting(false)
    if (r.status >= 400) return setErr(r.data?.message)
    consumeAuthResponse(r.data)
  }

  // Forgot password / passwordless sign-in. Emails a one-time code and
  // drops the customer on the existing code-entry step. Reuses the same
  // request-code + login-code endpoints as the 2FA path — no separate
  // reset flow needed. Once in, they can set a new password from Account →
  // Security. The API always 200s (anti-enumeration), so we never reveal
  // whether the email has an account.
  async function forgotPassword() {
    setErr(null)
    if (!email) { setErr('Enter your email above first, then tap "Email me a sign-in code".'); return }
    setSubmitting(true)
    await API.post('/sessions/request-code', { email, resellerSlug: brand?.slug })
    setSubmitting(false)
    setStep('code')
  }

  return (
    <Shell>
      <h1 style={{ fontSize: 22, marginTop: 0 }}>Sign in to manage your trip</h1>
      {step === 'request' && (
        <>
          <p style={{ color: '#64748b', fontSize: 13.5, margin: '0 0 12px', lineHeight: 1.5 }}>Enter your email and we'll send you a 6-digit sign-in code.</p>
          <form onSubmit={e => { e.preventDefault(); forgotPassword() }} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} style={inp} required />
            {err && <div style={errBox}>{err}</div>}
            <button type="submit" disabled={submitting} style={primaryBtn}>{submitting ? 'Sending...' : 'Email me a sign-in code'}</button>
          </form>
          <div style={{ marginTop: 12, textAlign: 'center' }}>
            <button type="button" onClick={() => { setErr(null); setStep('password') }} style={linkBtn}>Sign in with a password instead</button>
          </div>
          <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid #f1f5f9', color: '#64748b', fontSize: 13, lineHeight: 1.5 }}>
            <strong style={{ color: '#334155' }}>First time here?</strong> Your account is created automatically the first
            time you open a link from one of your booking emails — just tap
            <em> View your booking</em> or <em>Review and decide</em> in the most
            recent email we sent you.
          </div>
        </>
      )}
      {step === 'password' && (
        <>
          <form onSubmit={loginPwd} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} style={inp} required />
            <input type="password" placeholder="Password" value={pwd} onChange={e => setPwd(e.target.value)} style={inp} required />
            {err && <div style={errBox}>{err}</div>}
            <button type="submit" disabled={submitting} style={primaryBtn}>{submitting ? 'Signing in...' : 'Sign in'}</button>
          </form>
          <div style={{ marginTop: 12, textAlign: 'center' }}>
            <button type="button" onClick={forgotPassword} disabled={submitting} style={linkBtn}>
              Forgot your password? Email me a sign-in code
            </button>
          </div>
          <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid #f1f5f9', color: '#64748b', fontSize: 13, lineHeight: 1.5 }}>
            <strong style={{ color: '#334155' }}>First time here?</strong> Your account is created automatically the first
            time you open a link from one of your booking emails — just tap
            <em> View your booking</em> or <em>Review and decide</em> in the most
            recent email we sent you, and you'll be asked to set a password.
          </div>
        </>
      )}
      {step === 'code' && (
        <form onSubmit={loginCode} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <p style={{ color: '#64748b', fontSize: 13, margin: 0 }}>We sent a 6-digit code to <strong>{email}</strong>. It expires in 10 minutes.</p>
          <p style={{ color: '#94a3b8', fontSize: 12.5, margin: 0, lineHeight: 1.45 }}>Didn't get it? Check your junk/spam folder.</p>
          <input type="text" placeholder="Enter 6-digit code" value={code} onChange={e => setCode(e.target.value)} style={inp} maxLength={6} required />
          <label style={{ fontSize: 13, color: '#475569' }}>
            <input type="checkbox" checked={trustDevice} onChange={e => setTrustDevice(e.target.checked)} /> Remember this device for 90 days
          </label>
          {err && <div style={errBox}>{err}</div>}
          <button type="submit" disabled={submitting} style={primaryBtn}>{submitting ? 'Verifying...' : 'Verify and sign in'}</button>
        </form>
      )}
      {step === 'mfa' && (
        <form onSubmit={verifyMfa} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <p style={{ color: '#64748b', fontSize: 13, margin: 0 }}>Two-factor authentication is on for this account. Enter the 6-digit code from your authenticator app, or one of your backup codes.</p>
          <input type="text" placeholder="000000 or XXXXX-XXXXX" value={mfaCode} onChange={e => setMfaCode(e.target.value)} style={{ ...inp, fontFamily: 'monospace', letterSpacing: 2 }} maxLength={11} required autoFocus />
          {err && <div style={errBox}>{err}</div>}
          <button type="submit" disabled={submitting || !mfaCode} style={primaryBtn}>{submitting ? 'Verifying...' : 'Verify code'}</button>
        </form>
      )}
    </Shell>
  )
}

export function CustomerTripsList() {
  const [trips, setTrips] = useState(null)
  const [err, setErr] = useState(null)
  const [deletingId, setDeletingId] = useState(null)
  const [tab, setTab] = useState(null) // null = auto-pick highest-priority non-empty
  const navigate = useNavigate()
  const scopedPath = useScopedPath()
  const brand = useResellerBrand()
  const primary = brand?.primaryColor || '#0f172a'

  function reload() {
    return API.get('/trips', { headers: authHeaders() }).then(r => {
      if (r.status === 401) { navigate(scopedPath('login')); return }
      if (r.status >= 400) { setErr(r.data?.message); return }
      setTrips(r.data.trips)
    })
  }
  useEffect(() => { reload() // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function deleteTrip(id) {
    if (!confirm('Delete this trip? This only works on un-paid trips, and can\'t be undone.')) return
    setDeletingId(id); setErr(null)
    const r = await API.delete('/trips/' + id, { headers: authHeaders() })
    setDeletingId(null)
    if (r.status >= 400) { setErr(r.data?.message || 'Could not delete'); return }
    await reload()
  }

  if (err) return <Shell><Box>{err}</Box></Shell>
  if (!trips) return <Shell><Box>Loading your trips...</Box></Shell>
  if (trips.length === 0) return <Shell><Box>No trips yet.</Box></Shell>

  // Split trips by state. "Building" carts (resumeUrl) are unfinished; booked
  // trips fall into active / awaiting-payment / past. Each state is a tab so
  // nothing hides behind a collapsed header.
  const drafts = trips.filter(t => t.resumeUrl)
  const booked = trips.filter(t => !t.resumeUrl)
  const groups = [
    { key: 'active',   title: 'Active & upcoming',           short: 'Active',   items: booked.filter(t => t.bucket === 'in_progress') },
    { key: 'awaiting', title: 'Awaiting payment',            short: 'Awaiting', items: booked.filter(t => t.bucket === 'pending') },
    { key: 'drafts',   title: 'Unfinished — not booked yet', short: 'Unfinished', items: drafts },
    { key: 'past',     title: 'Past & cancelled',            short: 'Past',     items: booked.filter(t => t.bucket === 'completed') },
  ].filter(g => g.items.length)

  // Tabs = "All" + every non-empty state. Default to the highest-priority
  // populated state so the page opens focused, not on a wall of everything.
  const tabs = [{ key: 'all', short: 'All', items: trips }, ...groups]
  const activeTab = (tab && tabs.some(t => t.key === tab)) ? tab : (groups[0]?.key || 'all')
  const shownGroups = activeTab === 'all' ? groups : groups.filter(g => g.key === activeTab)

  function tripCard(t) {
    const img = resolveAssetUrl(t.heroImage)
    return (
      <Link key={t.id} to={scopedPath('trips/' + t.id)} className="rcp-trip-card" style={{
        textDecoration: 'none', color: 'inherit', display: 'block',
        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden',
      }}>
        {/* Hero banner — package image or a brand-coloured gradient */}
        <div style={{
          position: 'relative', height: 132,
          background: img ? `url('${img}') center/cover no-repeat` : `linear-gradient(135deg, ${primary} 0%, ${primary}cc 100%)`,
        }}>
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.5), rgba(0,0,0,0) 60%)' }} />
          <span style={{ position: 'absolute', top: 10, right: 10, ...statusPill(t.bucket), boxShadow: '0 1px 4px rgba(0,0,0,0.25)' }}>{t.status.replace(/_/g, ' ')}</span>
          <div style={{ position: 'absolute', left: 14, right: 14, bottom: 10, color: '#fff', fontWeight: 700, fontSize: 17, lineHeight: 1.25, textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>{t.packageName || 'Tee time booking'}</div>
        </div>
        {/* Body */}
        <div style={{ padding: 14 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: t.hotel ? 8 : 10 }}>
            <MetaChip icon="📅">{t.checkIn} → {t.checkOut}</MetaChip>
            {t.nights != null && <MetaChip icon="🌙">{t.nights} night{t.nights !== 1 ? 's' : ''}</MetaChip>}
            {t.rounds != null && <MetaChip icon="⛳">{t.rounds} round{t.rounds !== 1 ? 's' : ''}</MetaChip>}
            <MetaChip icon="👥">{t.numGolfers} golfer{t.numGolfers !== 1 ? 's' : ''}</MetaChip>
          </div>
          {t.hotel && <div style={{ fontSize: 13, color: '#475569', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}><span aria-hidden>🏨</span>{t.hotel}</div>}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 10, borderTop: '1px solid #f1f5f9' }}>
            <span style={{ color: '#94a3b8', fontSize: 12 }}>via {t.reseller.name}</span>
            <span style={{ color: primary, fontSize: 17, fontWeight: 800 }}>{fmtMoney(t.total, t.currency)}</span>
          </div>
          {t.status === 'awaiting_payment' && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #fcd34d', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span
                role="button"
                onClick={e => { e.preventDefault(); navigate(scopedPath('trips/' + t.id + '/pay')) }}
                style={{ display: 'inline-block', padding: '8px 14px', background: primary, color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
              >
                Complete payment →
              </span>
              <span style={{ color: '#b45309', fontSize: 12 }}>Card not yet authorized — nothing is reserved.</span>
            </div>
          )}
          {t.resumeUrl && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #e2e8f0', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span
                role="button"
                onClick={e => { e.preventDefault(); window.location.href = t.resumeUrl }}
                style={{ display: 'inline-block', padding: '8px 14px', background: primary, color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                Continue this trip →
              </span>
              <button
                type="button"
                onClick={e => { e.preventDefault(); e.stopPropagation(); deleteTrip(t.id) }}
                disabled={deletingId === t.id}
                style={{ padding: '8px 12px', background: '#fff', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                {deletingId === t.id ? 'Deleting...' : 'Delete'}
              </button>
              <span style={{ marginLeft: 'auto', color: '#94a3b8', fontSize: 11 }}>Picks up at the package; you'll re-enter golfer info.</span>
            </div>
          )}
        </div>
      </Link>
    )
  }

  return (
    <Shell>
      <style>{`
        .rcp-trip-card { transition: transform .15s ease, box-shadow .15s ease; }
        .rcp-trip-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(15,23,42,0.12); }
        .rcp-tabs { display: flex; gap: 6; overflow-x: auto; -webkit-overflow-scrolling: touch; scrollbar-width: none; margin: 4px 0 20px; padding-bottom: 2px; }
        .rcp-tabs::-webkit-scrollbar { display: none; }
      `}</style>
      <PortalNav active="trips" />
      <h1 style={{ fontSize: 22, marginTop: 0, marginBottom: 12 }}>Your trips</h1>

      {/* State tabs — each carries a live count; horizontal-scroll on mobile. */}
      <div className="rcp-tabs" role="tablist">
        {tabs.map(t => {
          const on = t.key === activeTab
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => setTab(t.key)}
              style={{
                flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 7,
                padding: '8px 14px', borderRadius: 999, cursor: 'pointer', whiteSpace: 'nowrap',
                fontSize: 13, fontWeight: 600,
                border: on ? `1px solid ${primary}` : '1px solid #e2e8f0',
                background: on ? primary : '#fff',
                color: on ? '#fff' : '#475569',
              }}
            >
              {t.short}
              <span style={{
                fontSize: 11, fontWeight: 700, borderRadius: 999, padding: '1px 7px', lineHeight: 1.5,
                background: on ? 'rgba(255,255,255,0.24)' : '#eef2f7',
                color: on ? '#fff' : '#64748b',
              }}>{t.items.length}</span>
            </button>
          )
        })}
      </div>

      {shownGroups.map(g => (
        <div key={g.key} style={{ marginBottom: 20 }}>
          {activeTab === 'all' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 13, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>{g.title}</span>
              <span style={{ fontSize: 11, color: '#64748b', background: '#e2e8f0', borderRadius: 999, padding: '1px 8px', fontWeight: 600 }}>{g.items.length}</span>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {g.items.map(t => tripCard(t))}
          </div>
        </div>
      ))}
    </Shell>
  )
}

// Booking statuses where the customer cannot self-cancel (already done
// or in a terminal state). Anything else gets a "Cancel trip" button.
const NON_CANCELLABLE = new Set([
  'cancelled_by_customer', 'cancelled_by_provider', 'cancelled_failed_payment',
  'cancelled', 'completed', 'refunded', 'partial_refund', 'no_show', 'failed',
])

export function CustomerTripDetail() {
  const { id } = useParams()
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const [busy, setBusy] = useState(null) // 'cancel' | 'approve:<id>' | 'alt:<id>' | null
  const [confirmCancel, setConfirmCancel] = useState(false)
  const navigate = useNavigate()
  const scopedPath = useScopedPath()

  function reload() {
    return API.get('/trips/' + id, { headers: authHeaders() }).then(r => {
      if (r.status === 401) { navigate(scopedPath('login')); return }
      if (r.status >= 400) { setErr(r.data?.message); return }
      setData(r.data)
    })
  }

  useEffect(() => { reload() // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  async function doCancel() {
    setBusy('cancel'); setErr(null)
    const r = await API.post('/trips/' + id + '/cancel', {}, { headers: authHeaders() })
    setBusy(null); setConfirmCancel(false)
    if (r.status >= 400) return setErr(r.data?.message || 'Cancel failed')
    await reload()
  }

  async function approveDelta(componentId) {
    setBusy('approve:' + componentId); setErr(null)
    const r = await API.post('/trips/' + id + '/actions/' + componentId + '/approve-delta', {}, { headers: authHeaders() })
    setBusy(null)
    if (r.status >= 400) return setErr(r.data?.message || 'Could not approve')
    await reload()
  }

  async function pickAlternateQuick(componentId) {
    setBusy('alt:' + componentId); setErr(null)
    // Empty body: backend kicks the sequencer to retry with whatever's
    // currently best-available. Used for hotel components and as the
    // fallback when the modal isn't applicable.
    const r = await API.post('/trips/' + id + '/actions/' + componentId + '/pick-alternate', {}, { headers: authHeaders() })
    setBusy(null)
    if (r.status >= 400) return setErr(r.data?.message || 'Could not retry')
    await reload()
  }
  async function pickAlternateWith(componentId, body) {
    setBusy('alt:' + componentId); setErr(null)
    const r = await API.post('/trips/' + id + '/actions/' + componentId + '/pick-alternate', body, { headers: authHeaders() })
    setBusy(null)
    if (r.status >= 400) return setErr(r.data?.message || 'Could not retry')
    await reload()
  }

  if (err && !data) return <Shell><Box>{err}</Box></Shell>
  if (!data) return <Shell><Box>Loading...</Box></Shell>

  const { trip, components, rounds, actionItems, payments = [] } = data
  const canCancel = !NON_CANCELLABLE.has(trip.status)

  async function resendInvite(paymentId) {
    setBusy('resend:' + paymentId); setErr(null)
    const r = await API.post('/trips/' + id + '/payments/' + paymentId + '/resend-invite', {}, { headers: authHeaders() })
    setBusy(null)
    if (r.status >= 400) return setErr(r.data?.message || 'Could not resend')
    await reload()
  }

  return <TripDetailContent
    id={id} data={data} reload={reload}
    busy={busy} setBusy={setBusy} err={err} setErr={setErr}
    confirmCancel={confirmCancel} setConfirmCancel={setConfirmCancel}
    doCancel={doCancel} approveDelta={approveDelta}
    pickAlternateQuick={pickAlternateQuick} pickAlternateWith={pickAlternateWith}
    resendInvite={resendInvite} canCancel={canCancel}
    scopedPath={scopedPath}
  />
}

function TripDetailContent({ id, data, reload, busy, setBusy, err, setErr, confirmCancel, setConfirmCancel, doCancel, approveDelta, pickAlternateQuick, pickAlternateWith, resendInvite, canCancel, scopedPath }) {
  const { trip, components, rounds, actionItems, payments = [], alternateCourses = [] } = data
  const brand = useResellerBrand()
  const primary = brand?.primaryColor || '#0f172a'
  const heroImg = resolveAssetUrl(trip.heroImage)
  // Standalone tee-time booking: no package, no hotel — leaner header (single
  // date, no rooms) and a generic title.
  const isTeeTimeOnly = !trip.packageName && !trip.hotel?.name
  const [messages, setMessages] = useState(null)
  const [newMsg, setNewMsg] = useState('')
  // When set, opens the alternate-picker modal for a specific component.
  // Holds { componentId, type: 'hotel'|'round', round? } — round info
  // lets the modal default values to the current selection.
  const [altPicker, setAltPicker] = useState(null)

  // Change-request workflow state. requestMode toggles the per-line
  // dropdowns on the existing component cards. requestItems is keyed
  // by 'hotel:<componentId>' or 'round:<roundIndex>'. Empty action
  // means "Keep the same" (the default).
  const [requestMode, setRequestMode] = useState(false)
  const [requestItems, setRequestItems] = useState({})
  const [requestNotes, setRequestNotes] = useState('')
  const [requestSubmitted, setRequestSubmitted] = useState(false)
  const [pastRequests, setPastRequests] = useState(null)

  async function loadMessages() {
    const r = await API.get('/trips/' + id + '/messages', { headers: authHeaders() })
    if (r.status >= 400) return
    setMessages(r.data.messages)
  }

  async function loadChangeRequests() {
    const r = await API.get('/trips/' + id + '/change-requests', { headers: authHeaders() })
    if (r.status >= 400) return
    setPastRequests(r.data.requests)
  }

  useEffect(() => { loadMessages(); loadChangeRequests() // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  async function sendMessage(e) {
    e.preventDefault()
    if (!newMsg.trim()) return
    setBusy('msg'); setErr(null)
    const r = await API.post('/trips/' + id + '/messages', { body: newMsg }, { headers: authHeaders() })
    setBusy(null)
    if (r.status >= 400) return setErr(r.data?.message || 'Could not send')
    setNewMsg('')
    await loadMessages()
  }

  function startRequestMode() {
    setRequestMode(true)
    setRequestItems({})
    setRequestNotes('')
    setRequestSubmitted(false)
    setErr(null)
  }
  function cancelRequestMode() {
    setRequestMode(false)
    setRequestItems({})
    setRequestNotes('')
  }
  function setItemAction(key, action) {
    setRequestItems(prev => ({ ...prev, [key]: { ...(prev[key] || {}), action } }))
  }
  function setItemNotes(key, notes) {
    setRequestItems(prev => ({ ...prev, [key]: { ...(prev[key] || {}), notes } }))
  }

  async function sendRequests() {
    setBusy('reqsubmit'); setErr(null)
    const items = []
    // Hotel
    const hotelComp = components.find(c => c.ComponentType === 'hotel')
    if (hotelComp) {
      const key = 'hotel:' + hotelComp.ID_NO
      const item = requestItems[key] || {}
      if (item.action && item.action !== 'keep') {
        items.push({
          componentType: 'hotel',
          componentId:   hotelComp.ID_NO,
          label:         `Hotel - ${trip.hotel.name}`,
          action:        item.action,
          notes:         (item.notes || '').trim() || null,
        })
      }
    }
    // Rounds
    for (const r of rounds) {
      const key = 'round:' + r.RoundIndex
      const item = requestItems[key] || {}
      if (item.action && item.action !== 'keep') {
        const comp = components.find(c => c.ComponentType === 'round' && c.Sequence === r.RoundIndex)
        items.push({
          componentType: 'round',
          componentId:   comp?.ID_NO || null,
          roundIndex:    r.RoundIndex,
          label:         `Round ${r.RoundIndex + 1} - ${r.CourseName} on ${r.PlayDate} at ${r.TeeTime}`,
          action:        item.action,
          notes:         (item.notes || '').trim() || null,
        })
      }
    }
    const additionalNotes = requestNotes.trim() || null
    if (!items.length && !additionalNotes) {
      setBusy(null); setErr('Pick a change on at least one line, or write a note below.'); return
    }
    const resp = await API.post('/trips/' + id + '/change-requests', { items, additionalNotes }, { headers: authHeaders() })
    setBusy(null)
    if (resp.status >= 400) return setErr(resp.data?.message || 'Could not send request')
    setRequestSubmitted(true)
    setRequestMode(false)
    setRequestItems({})
    setRequestNotes('')
    await loadChangeRequests()
  }

  // Per-line dropdown + conditional "Other" textarea. `itemKey` is
  // 'hotel:<id>' or 'round:<index>'. Rendered inside each component
  // card when requestMode is true.
  function RequestControls({ itemKey }) {
    const cur = requestItems[itemKey] || {}
    const action = cur.action || 'keep'
    return (
      <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed #cbd5e1' }}>
        <label style={{ fontSize: 12, color: '#475569', fontWeight: 600, display: 'block', marginBottom: 4 }}>What would you like to do?</label>
        <select
          value={action}
          onChange={e => setItemAction(itemKey, e.target.value)}
          style={{ ...inp, padding: '7px 10px' }}
        >
          <option value="keep">Keep the same</option>
          <option value="change_datetime">Change date / time</option>
          <option value="cancel">Cancel this item</option>
          <option value="other">Other (explain below)</option>
        </select>
        {action === 'other' && (
          <textarea
            value={cur.notes || ''}
            onChange={e => setItemNotes(itemKey, e.target.value)}
            placeholder="Tell us what you need..."
            rows={2}
            maxLength={2000}
            style={{ ...inp, marginTop: 6, resize: 'vertical', fontFamily: 'inherit' }}
          />
        )}
        {action === 'change_datetime' && (
          <textarea
            value={cur.notes || ''}
            onChange={e => setItemNotes(itemKey, e.target.value)}
            placeholder="Preferred new date / time (optional)"
            rows={2}
            maxLength={2000}
            style={{ ...inp, marginTop: 6, resize: 'vertical', fontFamily: 'inherit' }}
          />
        )}
      </div>
    )
  }

  return (
    <Shell>
      <Link to={scopedPath('trips')} style={{ color: '#64748b', fontSize: 13, textDecoration: 'none', display: 'inline-block', marginBottom: 12 }}>← All trips</Link>

      {/* Bold hero — package image or a brand-coloured gradient, with the
          package name, reseller, status and a quick summary overlaid. */}
      <div style={{
        position: 'relative', borderRadius: 16, overflow: 'hidden', minHeight: 190,
        background: heroImg ? `url('${heroImg}') center/cover no-repeat` : `linear-gradient(135deg, ${primary} 0%, ${primary}cc 100%)`,
      }}>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.74) 0%, rgba(0,0,0,0.2) 55%, rgba(0,0,0,0.05) 100%)' }} />
        <span style={{ position: 'absolute', top: 14, right: 14, ...statusPill(trip.bucket), boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }}>{trip.status.replace(/_/g, ' ')}</span>
        <div style={{ position: 'absolute', left: 18, right: 18, bottom: 16, color: '#fff' }}>
          <div style={{ fontSize: 12, fontWeight: 600, opacity: 0.9, textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>via {trip.resellerName}</div>
          <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1.15, marginTop: 3, textShadow: '0 1px 6px rgba(0,0,0,0.6)' }}>{trip.packageName || (isTeeTimeOnly ? 'Tee time booking' : 'Your trip')}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
            <HeroChip icon="📅">{isTeeTimeOnly ? trip.checkIn : `${trip.checkIn} → ${trip.checkOut}`}</HeroChip>
            {trip.nights != null && <HeroChip icon="🌙">{trip.nights} night{trip.nights !== 1 ? 's' : ''}</HeroChip>}
            {trip.rounds != null && <HeroChip icon="⛳">{trip.rounds} round{trip.rounds !== 1 ? 's' : ''}</HeroChip>}
            <HeroChip icon="👥">{trip.numGolfers} golfer{trip.numGolfers !== 1 ? 's' : ''}</HeroChip>
          </div>
        </div>
      </div>

      {/* Summary strip — hotel + total price in the brand colour. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginTop: 14, padding: '14px 16px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 12 }}>
        <div style={{ minWidth: 0, flex: '1 1 200px' }}>
          {trip.hotel?.name && <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}><span aria-hidden>🏨</span>{trip.hotel.name}</div>}
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
            {trip.numGolfers} golfer{trip.numGolfers !== 1 ? 's' : ''}{isTeeTimeOnly ? '' : ` · ${trip.numRooms} room${trip.numRooms !== 1 ? 's' : ''}`}
          </div>
        </div>
        <div style={{ textAlign: 'right', marginLeft: 'auto' }}>
          <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>Total</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: primary }}>{fmtMoney(trip.total, trip.currency)}</div>
        </div>
      </div>

      {/* Payment needed — the leader's card was never authorized. Offer a
          self-serve way to finish paying (reuses the embed pay endpoint). */}
      {trip.canCompletePayment && (
        <div style={{ marginTop: 16, padding: 16, background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 12 }}>
          <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 4 }}>Payment needed to confirm this trip</div>
          <div style={{ fontSize: 13, color: '#78350f', marginBottom: 12 }}>
            We haven't been able to authorize a card for this booking yet, so nothing is reserved. Add a card to lock in your hotel and tee times — {fmtMoney(trip.amountDue, trip.currency)}.
          </div>
          <Link to={scopedPath('trips/' + id + '/pay')} style={{ display: 'inline-block', padding: '10px 18px', background: primary, color: '#fff', borderRadius: 8, fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>
            Complete payment →
          </Link>
        </div>
      )}

      {err && data && <div style={{ ...errBox, marginTop: 16 }}>{err}</div>}

      {actionItems.length > 0 && (
        <div style={{ background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 8, padding: 14, marginTop: 16 }}>
          <div style={{ fontWeight: 600, color: '#92400e', marginBottom: 8 }}>{actionItems.length} item{actionItems.length > 1 ? 's' : ''} need your attention</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {actionItems.map(a => (
              <div key={a.componentId} style={{ background: '#fff', border: '1px solid #fcd34d', borderRadius: 6, padding: 10 }}>
                <div style={{ fontSize: 13, color: '#78350f', marginBottom: 8 }}>{a.label}</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => approveDelta(a.componentId)}
                    disabled={busy === 'approve:' + a.componentId}
                    style={smallPrimaryBtn}
                  >
                    {busy === 'approve:' + a.componentId ? 'Working...' : 'Approve and proceed'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      // Hotel action items don't have alternate-courses;
                      // just kick the sequencer to retry.
                      if (a.type === 'hotel') return pickAlternateQuick(a.componentId)
                      // Round: open the modal so the customer can pick a
                      // different course / date / time.
                      const r = rounds.find(r => r.RoundIndex === a.sequence)
                      setAltPicker({ componentId: a.componentId, type: 'round', round: r })
                    }}
                    disabled={busy === 'alt:' + a.componentId}
                    style={smallSecondaryBtn}
                  >
                    {busy === 'alt:' + a.componentId ? 'Working...' : 'Try an alternate'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!requestMode && canCancel && (
        <Section title="Need to change something?">
          <div style={card}>
            <div style={{ fontSize: 14, color: '#475569', marginBottom: 10 }}>
              Pick one or more items below and we'll forward the request to the reseller.
            </div>
            <button type="button" onClick={startRequestMode} style={smallPrimaryBtn}>Request changes</button>
            {requestSubmitted && (
              <div style={{ marginTop: 10, color: '#065f46', fontSize: 13 }}>
                Your previous request was sent. The reseller will respond by email.
              </div>
            )}
          </div>
        </Section>
      )}

      <Section title="Hotel">
        {components.filter(c => c.ComponentType === 'hotel').map(c => (
          <div key={c.ID_NO} style={itemCard(primary)}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={itemIcon(primary)} aria-hidden>🏨</div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{trip.hotel.name}</div>
                <div style={{ marginTop: 6 }}><span style={componentPill(c.Status)}>{c.Status.replace(/_/g, ' ')}</span></div>
                {c.ProviderConfirmationNumber && <div style={{ color: '#64748b', fontSize: 12, marginTop: 6 }}>Confirmation: {c.ProviderConfirmationNumber}</div>}
              </div>
            </div>
            {requestMode && <RequestControls itemKey={'hotel:' + c.ID_NO} />}
          </div>
        ))}
      </Section>

      <Section title="Golf rounds">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rounds.map(r => {
            const comp = components.find(c => c.ComponentType === 'round' && c.Sequence === r.RoundIndex)
            return (
              <div key={r.RoundIndex} style={itemCard(primary)}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={itemIcon(primary)} aria-hidden>⛳</div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{r.CourseName}</div>
                    <div style={{ color: '#475569', fontSize: 13, marginTop: 2, display: 'flex', flexWrap: 'wrap', gap: '2px 8px' }}>
                      <span>📅 {r.PlayDate}</span><span>🕑 {r.TeeTime}</span>
                    </div>
                    <div style={{ marginTop: 6 }}><span style={componentPill(comp?.Status || r.Status)}>{(comp?.Status || r.Status || 'pending').replace(/_/g, ' ')}</span></div>
                    {comp?.ProviderConfirmationNumber && <div style={{ color: '#64748b', fontSize: 12, marginTop: 6 }}>Confirmation: {comp.ProviderConfirmationNumber}</div>}
                  </div>
                </div>
                {requestMode && <RequestControls itemKey={'round:' + r.RoundIndex} />}
              </div>
            )
          })}
        </div>
      </Section>

      {requestMode && (
        <Section title="Anything else?">
          <div style={card}>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 6 }}>Additional information (optional)</label>
            <textarea
              value={requestNotes}
              onChange={e => setRequestNotes(e.target.value)}
              placeholder="Add anything else the reseller should know..."
              rows={4}
              maxLength={4000}
              style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button type="button" onClick={sendRequests} disabled={busy === 'reqsubmit'} style={primaryBtn}>
                {busy === 'reqsubmit' ? 'Sending...' : 'Send Requests'}
              </button>
              <button type="button" onClick={cancelRequestMode} disabled={busy === 'reqsubmit'} style={smallSecondaryBtn}>Cancel</button>
            </div>
          </div>
        </Section>
      )}

      {!requestMode && pastRequests && pastRequests.length > 0 && (
        <Section title="Past change requests">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {pastRequests.map(r => (
              <div key={r.id} style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <div style={{ fontSize: 12, color: '#64748b' }}>{new Date(r.createdAt).toLocaleString()}</div>
                  <span style={requestStatusPill(r.status)}>{r.status.replace(/_/g, ' ')}</span>
                </div>
                {r.items && r.items.length > 0 && (
                  <ul style={{ margin: '6px 0', paddingLeft: 18, fontSize: 13, color: '#334155' }}>
                    {r.items.map(it => (
                      <li key={it.id}>
                        <strong>{it.label || (it.componentType === 'hotel' ? 'Hotel' : 'Round ' + ((it.roundIndex ?? 0) + 1))}</strong>
                        : {actionLabel(it.action)}
                        {it.notes && <div style={{ color: '#64748b', fontSize: 12 }}>{it.notes}</div>}
                      </li>
                    ))}
                  </ul>
                )}
                {r.additionalNotes && (
                  <div style={{ marginTop: 6, padding: 8, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 13, whiteSpace: 'pre-wrap', color: '#334155' }}>
                    {r.additionalNotes}
                  </div>
                )}
                {r.resolutionNotes && (
                  <div style={{ marginTop: 6, padding: 8, background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 4, fontSize: 13, color: '#065f46' }}>
                    <strong>Reseller response:</strong>{' '}{r.resolutionNotes}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {trip.status === 'completed' && (
        <Section title="Review">
          <div style={card}>
            <div style={{ fontSize: 14, color: '#475569', marginBottom: 10 }}>How was your trip? A short review helps other golfers and supports the reseller.</div>
            <Link to={scopedPath('trips/' + id + '/review')} style={{ display: 'inline-block', padding: '8px 14px', background: '#0f172a', color: '#fff', borderRadius: 6, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
              {data.hasReview ? 'Update your review' : 'Leave a review'}
            </Link>
          </div>
        </Section>
      )}

      {payments.length > 0 && (
        <Section title="Payments">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {payments.map(p => (
              <div key={p.id} style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    {p.payerName || p.payerEmail}
                    {p.isSplitLeader && <span style={{ marginLeft: 8, fontSize: 11, padding: '2px 6px', background: '#e0e7ff', color: '#3730a3', borderRadius: 4, fontWeight: 600 }}>BOOKING CUSTOMER</span>}
                  </div>
                  <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>{p.payerEmail}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{fmtMoney(p.amount, p.currency)}</div>
                  <div style={{ marginTop: 4 }}><span style={paymentPill(p.status)}>{p.status}</span></div>
                  {!p.isSplitLeader && p.status === 'pending' && (
                    <button
                      type="button"
                      onClick={() => resendInvite(p.id)}
                      disabled={busy === 'resend:' + p.id}
                      style={{ ...smallSecondaryBtn, marginTop: 6 }}
                    >
                      {busy === 'resend:' + p.id ? 'Sending...' : 'Resend invite'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section title="Messages">
        <div style={{ ...card, padding: 0 }}>
          <div style={{ maxHeight: 320, overflowY: 'auto', padding: 14, borderBottom: '1px solid #e2e8f0' }}>
            {messages === null && <div style={{ color: '#94a3b8', fontSize: 13 }}>Loading messages...</div>}
            {messages && messages.length === 0 && <div style={{ color: '#94a3b8', fontSize: 13 }}>No messages yet. Send the reseller a note about your trip below.</div>}
            {messages && messages.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {messages.map(m => (
                  <div key={m.id} style={{ display: 'flex', justifyContent: m.sender === 'customer' ? 'flex-end' : 'flex-start' }}>
                    <div style={{
                      maxWidth: '78%',
                      padding: '8px 12px',
                      borderRadius: 10,
                      background: m.sender === 'customer' ? '#0f172a' : '#f1f5f9',
                      color: m.sender === 'customer' ? '#fff' : '#0f172a',
                      fontSize: 13,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}>
                      <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.75, marginBottom: 2 }}>
                        {m.authorName || (m.sender === 'reseller' ? trip.resellerName : 'You')}
                      </div>
                      <div>{m.body}</div>
                      <div style={{ fontSize: 10, opacity: 0.6, marginTop: 4, textAlign: 'right' }}>
                        {new Date(m.sentAt).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <form onSubmit={sendMessage} style={{ padding: 12, display: 'flex', gap: 8 }}>
            <textarea
              value={newMsg}
              onChange={e => setNewMsg(e.target.value)}
              placeholder="Write a message to the reseller..."
              rows={2}
              maxLength={4000}
              style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }}
            />
            <button type="submit" disabled={busy === 'msg' || !newMsg.trim()} style={smallPrimaryBtn}>
              {busy === 'msg' ? 'Sending...' : 'Send'}
            </button>
          </form>
        </div>
      </Section>

      {altPicker && altPicker.type === 'round' && (
        <AltCoursePickerModal
          round={altPicker.round}
          alternateCourses={alternateCourses}
          submitting={busy === 'alt:' + altPicker.componentId}
          onCancel={() => setAltPicker(null)}
          onSubmit={async ({ altCoursesId, altPlayDate, altTeeTime }) => {
            await pickAlternateWith(altPicker.componentId, { altCoursesId, altPlayDate, altTeeTime })
            setAltPicker(null)
          }}
        />
      )}

      {canCancel && (
        <Section title="Manage">
          {!confirmCancel ? (
            <button type="button" onClick={() => setConfirmCancel(true)} style={cancelBtn}>Cancel this trip</button>
          ) : (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 14 }}>
              <div style={{ fontWeight: 600, color: '#7f1d1d', marginBottom: 6 }}>Cancel this trip?</div>
              <div style={{ fontSize: 13, color: '#991b1b', marginBottom: 12 }}>
                Refunds follow each provider's policy. Non-refundable hotel rates won't be refunded; refundable rounds will be. You'll get an email confirmation.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={doCancel} disabled={busy === 'cancel'} style={cancelBtn}>
                  {busy === 'cancel' ? 'Cancelling...' : 'Yes, cancel my trip'}
                </button>
                <button type="button" onClick={() => setConfirmCancel(false)} disabled={busy === 'cancel'} style={smallSecondaryBtn}>Keep it</button>
              </div>
            </div>
          )}
        </Section>
      )}
    </Shell>
  )
}

/**
 * Self-serve "Complete payment" for a booking stuck in awaiting_payment —
 * the leader's card was never authorized (abandoned / declined). Reuses the
 * proven embed payment endpoint (Moneris Mode A raw-card pre-auth). On
 * success the backend flips the booking to payments_complete and kicks the
 * sequencer, so we bounce back to the trip page where the new state shows.
 */
export function CustomerTripPay() {
  const { id } = useParams()
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const [cardForm, setCardForm] = useState({ number: '', expiry: '', cvc: '', name: '' })
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const navigate = useNavigate()
  const scopedPath = useScopedPath()
  const brand = useResellerBrand()
  const primary = brand?.primaryColor || '#0f172a'

  useEffect(() => {
    API.get('/trips/' + id, { headers: authHeaders() }).then(r => {
      if (r.status === 401) { navigate(scopedPath('login')); return }
      if (r.status >= 400) { setErr(r.data?.message); return }
      setData(r.data)
    }) // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const trip = data?.trip

  // Card field formatting — mirrors the embed PayScreen so the experience
  // is identical to the original checkout.
  function setNum(v) { const d = v.replace(/\D/g, '').slice(0, 19); setCardForm(c => ({ ...c, number: d.replace(/(.{4})/g, '$1 ').trim() })) }
  function setExp(v) { const d = v.replace(/\D/g, '').slice(0, 4); setCardForm(c => ({ ...c, expiry: d.length >= 3 ? d.slice(0, 2) + ' / ' + d.slice(2) : d })) }
  function setCvc(v) { setCardForm(c => ({ ...c, cvc: v.replace(/\D/g, '').slice(0, 4) })) }
  const ready = cardForm.number.replace(/\s/g, '').length >= 13
    && cardForm.expiry.replace(/\D/g, '').length === 4
    && cardForm.cvc.length >= 3
    && cardForm.name.trim().length >= 2

  async function submit(e) {
    e.preventDefault()
    if (!ready || !trip) return
    setErr(null); setSubmitting(true)
    try {
      // Goes to the public embed endpoint (iframeKey-authed). submitPayment
      // reuses the leader's existing pending row and guards double-charge.
      const url = `${_apiOrigin}/api/public/reseller-embed/${encodeURIComponent(trip.iframeKey)}/bookings/${trip.id}/payments`
      const r = await axios.post(url, {
        card: { number: cardForm.number, expiry: cardForm.expiry, cvc: cardForm.cvc, name: cardForm.name },
        amount: trip.amountDue,
        payerName: cardForm.name || trip.payerName,
        payerEmail: trip.payerEmail,
      }, { validateStatus: () => true })
      if (r.status >= 400) { setErr(r.data?.message || 'Card declined. Please check the details and try again.'); setSubmitting(false); return }
      setDone(true)
      // Sequencer runs server-side; give it a beat, then return to the trip.
      setTimeout(() => navigate(scopedPath('trips/' + id)), 2600)
    } catch (e2) {
      setErr(e2.message || 'Payment failed'); setSubmitting(false)
    }
  }

  if (err && !data) return <Shell><Box>{err}<div style={{ marginTop: 12 }}><Link to={scopedPath('trips')} style={{ color: primary, fontWeight: 600 }}>← All trips</Link></div></Box></Shell>
  if (!trip) return <Shell><Box>Loading…</Box></Shell>

  const backLink = <Link to={scopedPath('trips/' + id)} style={{ color: '#64748b', fontSize: 13, textDecoration: 'none', display: 'inline-block', marginBottom: 14 }}>← Back to trip</Link>

  if (done) {
    return (
      <Shell>
        <div style={{ ...card, borderColor: '#a7f3d0', background: '#ecfdf5', textAlign: 'center', padding: 28 }}>
          <div style={{ fontSize: 40, marginBottom: 8 }} aria-hidden>✅</div>
          <h1 style={{ fontSize: 22, margin: '0 0 6px', color: '#065f46' }}>Payment authorized</h1>
          <p style={{ color: '#047857', fontSize: 14, margin: 0 }}>
            We've authorized {fmtMoney(trip.amountDue, trip.currency)} and we're locking in your hotel and tee times now. Taking you back to your trip…
          </p>
        </div>
      </Shell>
    )
  }

  // Nothing owed (already paid, cancelled, or already booked).
  if (!trip.canCompletePayment) {
    return (
      <Shell>
        {backLink}
        <div style={card}>
          <h1 style={{ fontSize: 20, marginTop: 0 }}>Nothing to pay right now</h1>
          <p style={{ color: '#475569', fontSize: 14 }}>
            This trip isn't awaiting a payment from you. If you think that's wrong, open the trip and message {trip.resellerName}.
          </p>
          <Link to={scopedPath('trips/' + id)} style={{ display: 'inline-block', padding: '10px 16px', background: primary, color: '#fff', borderRadius: 8, fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>Open trip</Link>
        </div>
      </Shell>
    )
  }

  // Mode B (Stripe) bookings tokenize the card in-browser via Stripe Elements;
  // they can't use the raw-card form. Direct them to the emailed link / reseller.
  if (trip.gateway === 'stripe') {
    return (
      <Shell>
        {backLink}
        <div style={card}>
          <h1 style={{ fontSize: 20, marginTop: 0 }}>Finish your payment</h1>
          <p style={{ color: '#475569', fontSize: 14 }}>
            This booking uses a secure card form we can't show here. Please use the payment link {trip.resellerName} emailed you, or message them from your trip page and they'll re-send it.
          </p>
          <Link to={scopedPath('trips/' + id)} style={{ display: 'inline-block', padding: '10px 16px', background: primary, color: '#fff', borderRadius: 8, fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>Back to trip</Link>
        </div>
      </Shell>
    )
  }

  return (
    <Shell>
      {backLink}
      <h1 style={{ fontSize: 22, marginTop: 0, marginBottom: 4 }}>Complete your payment</h1>
      <div style={{ color: '#64748b', fontSize: 13, marginBottom: 16 }}>{trip.packageName || 'Tee time booking'} · via {trip.resellerName}</div>

      {/* Amount summary */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 16px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 12, marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: '#475569' }}>Amount to authorize</div>
        <div style={{ fontSize: 24, fontWeight: 800, color: primary }}>{fmtMoney(trip.amountDue, trip.currency)}</div>
      </div>

      <p style={{ color: '#64748b', fontSize: 13, marginTop: 0, marginBottom: 16 }}>
        Your card is <strong>authorized now, not charged</strong>. We capture the charge only once your hotel and tee times are locked in. If anything can't be confirmed, the authorization is voided.
      </p>

      {err && <div style={{ ...errBox, marginBottom: 14 }}>{err}</div>}

      <form onSubmit={submit}>
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 18 }}>
          <label style={payLabel}>Cardholder name</label>
          <input type="text" required autoComplete="cc-name" value={cardForm.name} onChange={e => setCardForm(c => ({ ...c, name: e.target.value }))} style={inp} />
          <label style={payLabel}>Card number</label>
          <input type="text" required inputMode="numeric" autoComplete="cc-number" placeholder="1234 5678 9012 3456" value={cardForm.number} onChange={e => setNum(e.target.value)} style={{ ...inp, fontFamily: 'monospace', letterSpacing: 1 }} />
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={payLabel}>Expiry (MM/YY)</label>
              <input type="text" required inputMode="numeric" autoComplete="cc-exp" placeholder="08 / 28" value={cardForm.expiry} onChange={e => setExp(e.target.value)} style={{ ...inp, fontFamily: 'monospace' }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={payLabel}>CVC</label>
              <input type="text" required inputMode="numeric" autoComplete="cc-csc" placeholder="123" value={cardForm.cvc} onChange={e => setCvc(e.target.value)} style={{ ...inp, fontFamily: 'monospace' }} />
            </div>
          </div>
        </div>
        <button
          type="submit"
          disabled={submitting || !ready}
          style={{ width: '100%', padding: 16, marginTop: 16, background: submitting || !ready ? '#cbd5e1' : primary, color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: submitting || !ready ? 'not-allowed' : 'pointer' }}
        >
          {submitting ? 'Authorizing…' : `Authorize ${fmtMoney(trip.amountDue, trip.currency)}`}
        </button>
        <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', marginTop: 10 }}>
          Secured by InteGolf. Card details are sent over HTTPS and processed through Moneris — never stored.
        </div>
      </form>
    </Shell>
  )
}
const payLabel = { display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', margin: '10px 0 4px' }

/**
 * Top-level portal nav — one identity spans Stay & Play trips AND prepaid
 * golf-bundle passes, so both live behind the same login. Shown on the two
 * "home" surfaces (trips list + passes list).
 */
// Session-scoped cache of the customer's quote count, so the nav doesn't
// re-fetch on every page. Keyed by session token to stay correct across
// account switches within the same tab.
let _quotesNav = null // { token, count } | null

function PortalNav({ active }) {
  const scopedPath = useScopedPath()
  const brand = useResellerBrand()
  const primary = brand?.primaryColor || '#0f172a'
  // Show the Quotes tab only when the customer actually has quotes (or is
  // currently viewing them, so the active tab never vanishes).
  const tok = (typeof localStorage !== 'undefined' && localStorage.getItem('rcp_session')) || ''
  const [hasQuotes, setHasQuotes] = useState(
    _quotesNav && _quotesNav.token === tok ? _quotesNav.count > 0 : active === 'quotes'
  )
  useEffect(() => {
    if (_quotesNav && _quotesNav.token === tok) { setHasQuotes(_quotesNav.count > 0); return }
    API.get('/quotes/count', { headers: authHeaders() }).then(r => {
      if (r.status >= 400) return
      const count = Number(r.data?.count) || 0
      _quotesNav = { token: tok, count }
      setHasQuotes(count > 0)
    }).catch(() => {}) // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tok])
  const items = [
    { key: 'trips', label: 'Trips', to: 'trips' },
    ...((hasQuotes || active === 'quotes') ? [{ key: 'quotes', label: 'Quotes', to: 'quotes' }] : []),
    { key: 'passes', label: 'Golf passes', to: 'passes' },
  ]
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
      {items.map(it => {
        const on = it.key === active
        return (
          <Link key={it.key} to={scopedPath(it.to)} style={{
            textDecoration: 'none', padding: '8px 16px', borderRadius: 999,
            fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap',
            border: on ? `1px solid ${primary}` : '1px solid #e2e8f0',
            background: on ? primary : '#fff', color: on ? '#fff' : '#475569',
          }}>{it.label}</Link>
        )
      })}
    </div>
  )
}

// Small status pill for passes.
function passStatusPill(pass) {
  let bg = '#dbeafe', fg = '#1e40af', label = 'Active'
  if (pass.expired || pass.roundsRemaining <= 0) { bg = '#e2e8f0'; fg = '#475569'; label = pass.expired ? 'Expired' : 'Used up' }
  else if (pass.status === 'pending_claim') { bg = '#fef3c7'; fg = '#92400e'; label = 'Unclaimed gift' }
  return <span style={{ display: 'inline-block', padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: bg, color: fg, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
}

// Rounds-remaining progress bar.
function RoundsBar({ pass, primary }) {
  const total = pass.roundsTotal || 0
  const left = Math.max(0, pass.roundsRemaining || 0)
  const pct = total ? Math.round((left / total) * 100) : 0
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{left} of {total} round{total !== 1 ? 's' : ''} left</span>
      </div>
      <div style={{ height: 8, background: '#eef2f7', borderRadius: 999, overflow: 'hidden' }}>
        <div style={{ width: pct + '%', height: '100%', background: primary, borderRadius: 999 }} />
      </div>
    </div>
  )
}

function PassCard({ pass }) {
  const scopedPath = useScopedPath()
  const brand = useResellerBrand()
  const primary = brand?.primaryColor || '#0f172a'
  const img = resolveAssetUrl(pass.heroImage)
  const courseNames = pass.courses.map(c => c.courseName)
  const shownCourses = courseNames.slice(0, 3)
  const extra = courseNames.length - shownCourses.length
  return (
    <Link to={scopedPath('passes/' + pass.id)} className="rcp-trip-card" style={{
      textDecoration: 'none', color: 'inherit', display: 'block',
      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden',
    }}>
      <div style={{
        position: 'relative', height: 96,
        background: img ? `url('${img}') center/cover no-repeat` : `linear-gradient(135deg, ${primary} 0%, ${primary}cc 100%)`,
      }}>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.5), rgba(0,0,0,0) 65%)' }} />
        <span style={{ position: 'absolute', top: 10, right: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.25)' }}>{passStatusPill(pass)}</span>
        <div style={{ position: 'absolute', left: 14, right: 14, bottom: 10, color: '#fff', fontWeight: 700, fontSize: 16, textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>{pass.bundleName}</div>
      </div>
      <div style={{ padding: 14 }}>
        <RoundsBar pass={pass} primary={primary} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
          {shownCourses.map((c, i) => <MetaChip key={i} icon="⛳">{c}</MetaChip>)}
          {extra > 0 && <MetaChip icon="＋">{extra} more</MetaChip>}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 10, borderTop: '1px solid #f1f5f9' }}>
          <span style={{ color: '#94a3b8', fontSize: 12 }}>via {pass.reseller.name}{pass.expiresAt ? ` · expires ${String(pass.expiresAt).slice(0, 10)}` : ''}</span>
          <span style={{ color: primary, fontSize: 12, fontWeight: 700 }}>View →</span>
        </div>
      </div>
    </Link>
  )
}

export function CustomerPassesList() {
  const [passes, setPasses] = useState(null)
  const [err, setErr] = useState(null)
  const navigate = useNavigate()
  const scopedPath = useScopedPath()

  useEffect(() => {
    API.get('/passes', { headers: authHeaders() }).then(r => {
      if (r.status === 401) { navigate(scopedPath('login')); return }
      if (r.status >= 400) { setErr(r.data?.message); return }
      setPasses(r.data.passes)
    }) // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (err) return <Shell><PortalNav active="passes" /><Box>{err}</Box></Shell>
  if (!passes) return <Shell><PortalNav active="passes" /><Box>Loading your passes…</Box></Shell>

  return (
    <Shell>
      <style>{`
        .rcp-trip-card { transition: transform .15s ease, box-shadow .15s ease; }
        .rcp-trip-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(15,23,42,0.12); }
      `}</style>
      <PortalNav active="passes" />
      <h1 style={{ fontSize: 22, marginTop: 0, marginBottom: 14 }}>Golf passes</h1>
      {passes.length === 0 ? (
        <Box>You don't have any prepaid-round passes yet. When you buy a golf bundle, it'll appear here — ready to book rounds against.</Box>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {passes.map(p => <PassCard key={p.id} pass={p} />)}
        </div>
      )}
    </Shell>
  )
}

// ─── Custom-trip QUOTES ─────────────────────────────────────────────────
// The sales/AR layer (reseller_quotes). These live at /quote/:token; the tab
// lets a logged-in customer find quotes their travel provider sent them.

function quoteStatusMeta(status) {
  const map = {
    sent:               { bg: '#fef3c7', fg: '#92400e', label: 'Awaiting you' },
    changes_requested:  { bg: '#fee2e2', fg: '#991b1b', label: 'Changes requested' },
    accepted:           { bg: '#dbeafe', fg: '#1e40af', label: 'Accepted' },
    accepted_partial:   { bg: '#dbeafe', fg: '#1e40af', label: 'Deposit paid' },
    accepted_paid:      { bg: '#d1fae5', fg: '#065f46', label: 'Paid in full' },
    declined_cancelled: { bg: '#e2e8f0', fg: '#475569', label: 'Cancelled' },
    voided:             { bg: '#e2e8f0', fg: '#475569', label: 'Voided' },
    expired:            { bg: '#e2e8f0', fg: '#475569', label: 'Expired' },
  }
  return map[status] || { bg: '#e2e8f0', fg: '#475569', label: String(status || '').replace(/_/g, ' ') }
}

function QuoteCard({ q }) {
  const brand = useResellerBrand()
  const primary = brand?.primaryColor || '#0f172a'
  const meta = quoteStatusMeta(q.status)
  const paid = Number(q.paidToDate) || 0
  const hasTotal = q.total != null
  const startDate = q.tripStartDate ? String(q.tripStartDate).slice(0, 10) : null
  return (
    <Link to={'/quote/' + q.publicToken} className="rcp-trip-card" style={{
      textDecoration: 'none', color: 'inherit', display: 'block',
      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 16,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{q.name || 'Custom trip'}</div>
        <span style={{ display: 'inline-block', padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: meta.bg, color: meta.fg, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>{meta.label}</span>
      </div>
      {q.quoteNumber && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{q.quoteNumber}</div>}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
        {startDate && <MetaChip icon="📅">{startDate}</MetaChip>}
        {q.numDays != null && <MetaChip icon="🌙">{q.numDays} night{q.numDays !== 1 ? 's' : ''}</MetaChip>}
        {q.numGolfers != null && <MetaChip icon="👥">{q.numGolfers} golfer{q.numGolfers !== 1 ? 's' : ''}</MetaChip>}
        {q.destination && <MetaChip icon="📍">{q.destination}</MetaChip>}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 10, borderTop: '1px solid #f1f5f9' }}>
        <span style={{ color: '#94a3b8', fontSize: 12 }}>via {q.reseller?.name || 'your provider'}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: primary }}>
          {paid > 0 && hasTotal
            ? `${fmtMoney(paid, q.currency)} paid of ${fmtMoney(q.total)}`
            : hasTotal ? fmtMoney(q.total, q.currency) : 'View'} →
        </span>
      </div>
    </Link>
  )
}

export function CustomerQuotesList() {
  const [quotes, setQuotes] = useState(null)
  const [err, setErr] = useState(null)
  const navigate = useNavigate()
  const scopedPath = useScopedPath()

  useEffect(() => {
    API.get('/quotes', { headers: authHeaders() }).then(r => {
      if (r.status === 401) { navigate(scopedPath('login')); return }
      if (r.status >= 400) { setErr(r.data?.message); return }
      setQuotes(r.data.quotes)
    }) // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (err) return <Shell><PortalNav active="quotes" /><Box>{err}</Box></Shell>
  if (!quotes) return <Shell><PortalNav active="quotes" /><Box>Loading your quotes…</Box></Shell>

  return (
    <Shell>
      <style>{`
        .rcp-trip-card { transition: transform .15s ease, box-shadow .15s ease; }
        .rcp-trip-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(15,23,42,0.12); }
      `}</style>
      <PortalNav active="quotes" />
      <h1 style={{ fontSize: 22, marginTop: 0, marginBottom: 14 }}>Your quotes</h1>
      {quotes.length === 0 ? (
        <Box>You don't have any custom-trip quotes yet. When your travel provider sends you a trip proposal, it'll appear here — ready to review, accept, and pay.</Box>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {quotes.map(q => <QuoteCard key={q.id} q={q} />)}
        </div>
      )}
    </Shell>
  )
}

export function CustomerPassDetail() {
  const { id } = useParams()
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const [cancellingId, setCancellingId] = useState(null)
  const navigate = useNavigate()
  const scopedPath = useScopedPath()
  const brand = useResellerBrand()
  const primary = brand?.primaryColor || '#0f172a'

  function load() {
    return API.get('/passes/' + id, { headers: authHeaders() }).then(r => {
      if (r.status === 401) { navigate(scopedPath('login')); return }
      if (r.status >= 400) { setErr(r.data?.message); return }
      setData(r.data)
    })
  }
  useEffect(() => { load() // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  async function cancelRound(redemptionId) {
    if (!confirm('Cancel this round? The round goes back onto your pass.')) return
    setCancellingId(redemptionId); setErr(null)
    const r = await API.post(`/passes/${id}/redemptions/${redemptionId}/cancel`, {}, { headers: authHeaders() })
    setCancellingId(null)
    if (r.status >= 400) { setErr(r.data?.message || 'Could not cancel'); return }
    await load()
  }

  if (err && !data) return <Shell><Box>{err}</Box></Shell>
  if (!data) return <Shell><Box>Loading…</Box></Shell>
  const pass = data.pass
  const heroImg = resolveAssetUrl(pass.heroImage)
  const canBook = pass.status === 'active' && !pass.expired && pass.roundsRemaining > 0

  return (
    <Shell>
      <Link to={scopedPath('passes')} style={{ color: '#64748b', fontSize: 13, textDecoration: 'none', display: 'inline-block', marginBottom: 12 }}>← Golf passes</Link>

      {/* Hero */}
      <div style={{
        position: 'relative', borderRadius: 16, overflow: 'hidden', minHeight: 150,
        background: heroImg ? `url('${heroImg}') center/cover no-repeat` : `linear-gradient(135deg, ${primary} 0%, ${primary}cc 100%)`,
      }}>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.2) 60%)' }} />
        <span style={{ position: 'absolute', top: 14, right: 14 }}>{passStatusPill(pass)}</span>
        <div style={{ position: 'absolute', left: 18, right: 18, bottom: 14, color: '#fff' }}>
          <div style={{ fontSize: 12, fontWeight: 600, opacity: 0.9, textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>via {pass.reseller.name}</div>
          <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.15, marginTop: 2, textShadow: '0 1px 6px rgba(0,0,0,0.6)' }}>{pass.bundleName}</div>
        </div>
      </div>

      {/* Rounds remaining + book CTA */}
      <div style={{ marginTop: 14, padding: '16px 16px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 12 }}>
        <RoundsBar pass={pass} primary={primary} />
        {pass.expiresAt && <div style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>Valid until {String(pass.expiresAt).slice(0, 10)}</div>}
        <div style={{ marginTop: 14 }}>
          {canBook ? (
            REDEEM_ENABLED ? (
              <Link to={scopedPath('passes/' + id + '/book')} style={{ display: 'inline-block', padding: '11px 18px', background: primary, color: '#fff', borderRadius: 8, fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>
                Book a round →
              </Link>
            ) : (
              <span style={{ display: 'inline-block', padding: '11px 18px', background: '#e2e8f0', color: '#64748b', borderRadius: 8, fontSize: 14, fontWeight: 700 }} title="Online round booking is being added next">
                Book a round (coming soon)
              </span>
            )
          ) : (
            <span style={{ fontSize: 13, color: '#64748b' }}>
              {pass.expired ? 'This pass has expired.' : pass.roundsRemaining <= 0 ? 'All rounds have been used.' : 'This pass isn\'t active yet.'}
            </span>
          )}
        </div>
      </div>

      <Section title="Where you can play">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {pass.courses.map(c => <MetaChip key={c.coursesId} icon="⛳">{c.courseName}</MetaChip>)}
        </div>
      </Section>

      <Section title="Rounds booked">
        {pass.redemptions.length === 0 ? (
          <Box>No rounds booked yet.</Box>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {pass.redemptions.map(r => (
              <div key={r.id} style={itemCard(primary)}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={itemIcon(primary)} aria-hidden>⛳</div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{r.courseName || 'Course #' + r.coursesId}</div>
                    <div style={{ color: '#475569', fontSize: 13, marginTop: 2, display: 'flex', flexWrap: 'wrap', gap: '2px 8px' }}>
                      <span>📅 {String(r.playDate).slice(0, 10)}</span>{r.teeTime && <span>🕑 {fmtTime12(String(r.teeTime).slice(11, 16))}</span>}
                    </div>
                    <div style={{ marginTop: 6 }}>
                      <span style={{ display: 'inline-block', padding: '3px 9px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: r.status === 'cancelled' ? '#fee2e2' : r.status === 'failed' ? '#fef3c7' : '#d1fae5', color: r.status === 'cancelled' ? '#991b1b' : r.status === 'failed' ? '#92400e' : '#065f46' }}>{r.status}</span>
                      {r.bookingRef && <span style={{ color: '#94a3b8', fontSize: 12, marginLeft: 8 }}>Ref: {r.bookingRef}</span>}
                    </div>
                  </div>
                  {r.status === 'booked' && (
                    <button
                      type="button"
                      onClick={() => cancelRound(r.id)}
                      disabled={cancellingId === r.id}
                      style={{ flexShrink: 0, padding: '7px 12px', background: '#fff', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                    >
                      {cancellingId === r.id ? 'Cancelling…' : 'Cancel'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {err && data && <div style={{ ...errBox, marginTop: 14 }}>{err}</div>}
    </Shell>
  )
}

// Tee-time buckets — mirror the embed's Early Morning / Morning / Afternoon /
// Late grouping so passes feel consistent with the package booking flow.
const PASS_TEE_BUCKETS = [
  { id: 'early',     label: 'Early Morning', sub: '6:00 – 9:00 AM',     startMin: 6 * 60,  endMin: 9 * 60 },
  { id: 'morning',   label: 'Morning',       sub: '9:00 AM – 12:00 PM', startMin: 9 * 60,  endMin: 12 * 60 },
  { id: 'afternoon', label: 'Afternoon',     sub: '12:00 – 3:00 PM',    startMin: 12 * 60, endMin: 15 * 60 },
  { id: 'late',      label: 'Late',          sub: '3:00 PM onward',     startMin: 15 * 60, endMin: 24 * 60 },
]
function teeMinutes(t) { const m = String(t || '').match(/(\d{1,2}):(\d{2})/); return m ? Number(m[1]) * 60 + Number(m[2]) : 0 }
function bucketForTee(t) { const mins = teeMinutes(t); return PASS_TEE_BUCKETS.find(b => mins >= b.startMin && mins < b.endMin)?.id || 'late' }
// Display tee times in 12-hour AM/PM (e.g. "19:10" -> "7:10 PM").
function fmtTime12(t) {
  const m = String(t || '').match(/(\d{1,2}):(\d{2})/)
  if (!m) return String(t || '')
  let h = Number(m[1]); const ap = h >= 12 ? 'PM' : 'AM'; h = h % 12 || 12
  return `${h}:${m[2]} ${ap}`
}

// Book a round against a prepaid pass: pick an eligible course + date, search
// live tee times, confirm (spends one round). Reuses the golf-booking engine
// server-side via /passes/:id/tee-times + /passes/:id/redeem.
export function CustomerPassBook() {
  const { id } = useParams()
  const [pass, setPass] = useState(null)
  const [err, setErr] = useState(null)
  const [coursesId, setCoursesId] = useState('')
  const [date, setDate] = useState('')
  const [slots, setSlots] = useState(null)      // null = not searched yet
  const [searching, setSearching] = useState(false)
  const [fallbackMsg, setFallbackMsg] = useState(null)
  const [selected, setSelected] = useState(null)
  const [booking, setBooking] = useState(false)
  const [bucket, setBucket] = useState('morning')
  const [golfers, setGolfers] = useState(1) // how many play this tee time (each spends 1 round)
  const [params] = useSearchParams()
  const token = params.get('token')          // no-login "Book Your Rounds" (pass-scoped)
  const guest = !!token
  const [booked, setBooked] = useState(null)  // focused-mode "You're booked!" state
  const navigate = useNavigate()
  const scopedPath = useScopedPath()
  const brand = useResellerBrand()
  const primary = brand?.primaryColor || '#0f172a'

  useEffect(() => {
    const url = guest ? `/guest/pass?token=${encodeURIComponent(token)}` : '/passes/' + id
    API.get(url, guest ? {} : { headers: authHeaders() }).then(r => {
      if (!guest && r.status === 401) { navigate(scopedPath('login')); return }
      if (r.status >= 400) { setErr(r.data?.message); return }
      setPass(r.data.pass)
      if (r.data.pass?.courses?.length === 1) setCoursesId(String(r.data.pass.courses[0].coursesId))
    }) // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token])

  async function search(e) {
    if (e) e.preventDefault()
    if (!coursesId || !date) { setErr('Pick a course and a date first.'); return }
    setSearching(true); setErr(null); setSlots(null); setSelected(null); setFallbackMsg(null)
    const url = guest
      ? `/guest/pass/tee-times?token=${encodeURIComponent(token)}&coursesId=${coursesId}&date=${date}&golfers=${golfers}`
      : `/passes/${id}/tee-times?coursesId=${coursesId}&date=${date}&golfers=${golfers}`
    const r = await API.get(url, guest ? {} : { headers: authHeaders() })
    setSearching(false)
    if (r.status >= 400) { setErr(r.data?.message || 'Search failed'); return }
    setSlots(r.data.slots || [])
    if (r.data.fallback) setFallbackMsg(r.data.message || 'Live tee-time search is unavailable for this course right now.')
  }

  async function confirmBooking() {
    if (!selected) return
    setBooking(true); setErr(null)
    const r = guest
      ? await API.post('/guest/pass/redeem', { token, coursesId: Number(coursesId), date, teeTime: selected.teeTime, ref: selected.ref, golfers })
      : await API.post(`/passes/${id}/redeem`, { coursesId: Number(coursesId), date, teeTime: selected.teeTime, ref: selected.ref, golfers }, { headers: authHeaders() })
    setBooking(false)
    if (r.status >= 400) { setErr(r.data?.message || 'Could not book this tee time.'); return }
    if (guest) {
      // Focused mode stays put: show a confirmation + let them book another.
      const courseName = pass.courses.find(c => String(c.coursesId) === String(coursesId))?.courseName
      setBooked({ course: courseName, date, teeTime: selected.teeTime, golfers, roundsRemaining: r.data.roundsRemaining })
      setPass(p => (p ? { ...p, roundsRemaining: r.data.roundsRemaining } : p))
      setSlots(null); setSelected(null)
      return
    }
    navigate(scopedPath('passes/' + id))
  }

  if (err && !pass) return <Shell focused={guest}><Box>{err}</Box></Shell>
  if (!pass) return <Shell focused={guest}><Box>Loading…</Box></Shell>

  const fmtTime = fmtTime12
  const todayStr = new Date().toISOString().slice(0, 10)
  const selCourse = pass.courses.find(c => String(c.coursesId) === String(coursesId))

  // Focused-mode "You're booked!" — stay in the single-pass screen, offer to
  // book another round or sign in for the full account.
  if (guest && booked) {
    return (
      <Shell focused>
        <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
          <div style={{ fontSize: 44, color: primary, lineHeight: 1, marginBottom: 8 }}>✓</div>
          <h1 style={{ fontSize: 22, margin: '0 0 6px' }}>You're booked!</h1>
          <p style={{ color: '#64748b', fontSize: 14, margin: '0 0 4px' }}>
            {booked.course} on <strong>{booked.date}</strong> at <strong>{fmtTime(booked.teeTime)}</strong> for {booked.golfers} golfer{booked.golfers > 1 ? 's' : ''}.
          </p>
          <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 22 }}>{booked.roundsRemaining} round{booked.roundsRemaining !== 1 ? 's' : ''} left on your pass.</p>
          {pass.roundsRemaining > 0 && (
            <button onClick={() => { setBooked(null); setDate(''); setSlots(null); setSelected(null) }} style={{ ...primaryBtn, background: primary }}>Book another round</button>
          )}
          <div style={{ marginTop: 16 }}>
            <Link to={scopedPath('login')} style={{ color: '#64748b', fontSize: 13 }}>Sign in to manage all your rounds →</Link>
          </div>
        </div>
      </Shell>
    )
  }

  return (
    <Shell focused={guest}>
      {!guest && <Link to={scopedPath('passes/' + id)} style={{ color: '#64748b', fontSize: 13, textDecoration: 'none', display: 'inline-block', marginBottom: 12 }}>← Back to pass</Link>}
      <h1 style={{ fontSize: 22, marginTop: 0, marginBottom: 4 }}>Book a round</h1>
      <div style={{ color: '#64748b', fontSize: 13, marginBottom: 16 }}>{pass.bundleName} · {pass.roundsRemaining} round{pass.roundsRemaining !== 1 ? 's' : ''} left</div>

      {err && <div style={{ ...errBox, marginBottom: 14 }}>{err}</div>}

      <form onSubmit={search} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 16, marginBottom: 16 }}>
        <label style={payLabel}>Course</label>
        <select value={coursesId} onChange={e => { setCoursesId(e.target.value); setSlots(null); setSelected(null) }} style={inp}>
          <option value="">Choose a course…</option>
          {pass.courses.map(c => <option key={c.coursesId} value={c.coursesId}>{c.courseName}</option>)}
        </select>
        <label style={payLabel}>Date</label>
        <input type="date" value={date} min={todayStr} onChange={e => { setDate(e.target.value); setSlots(null); setSelected(null) }} style={inp} />
        <label style={payLabel}>Golfers</label>
        <select value={golfers} onChange={e => { setGolfers(Number(e.target.value)); setSlots(null); setSelected(null) }} style={inp}>
          {Array.from({ length: Math.max(1, Math.min(4, pass.roundsRemaining)) }, (_, i) => i + 1).map(n => (
            <option key={n} value={n}>{n} golfer{n > 1 ? 's' : ''} · uses {n} round{n > 1 ? 's' : ''}</option>
          ))}
        </select>
        <button type="submit" disabled={searching || !coursesId || !date} style={{ width: '100%', padding: 13, marginTop: 14, background: searching || !coursesId || !date ? '#cbd5e1' : primary, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: searching || !coursesId || !date ? 'not-allowed' : 'pointer' }}>
          {searching ? 'Searching…' : 'Search tee times'}
        </button>
      </form>

      {fallbackMsg && <div style={{ padding: 12, background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, fontSize: 13, color: '#92400e', marginBottom: 14 }}>{fallbackMsg}</div>}

      {slots !== null && !fallbackMsg && (
        slots.length === 0 ? (
          <Box>No tee times available for that course and date. Try another day.</Box>
        ) : (() => {
          // Group into time-of-day windows, matching the package embed.
          const byBucket = { early: [], morning: [], afternoon: [], late: [] }
          for (const s of slots) byBucket[bucketForTee(s.teeTime)].push(s)
          let activeBucket = bucket
          if (byBucket[activeBucket].length === 0) {
            const first = PASS_TEE_BUCKETS.find(b => byBucket[b.id].length > 0)
            if (first) activeBucket = first.id
          }
          const visible = byBucket[activeBucket] || []
          return (
            <Section title={`Available tee times${selCourse ? ' — ' + selCourse.courseName : ''}`}>
              {/* Time-of-day windows */}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                {PASS_TEE_BUCKETS.map(b => {
                  const count = byBucket[b.id].length
                  const isActive = activeBucket === b.id
                  const isEmpty = count === 0
                  return (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => !isEmpty && setBucket(b.id)}
                      disabled={isEmpty}
                      style={{
                        padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                        display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2,
                        cursor: isEmpty ? 'not-allowed' : 'pointer',
                        background: isActive ? primary : '#fff',
                        color: isActive ? '#fff' : (isEmpty ? '#cbd5e1' : '#0f172a'),
                        border: '1px solid ' + (isActive ? primary : '#e2e8f0'),
                      }}
                    >
                      <span>{b.label}</span>
                      <span style={{ fontSize: 10, fontWeight: 400, opacity: 0.85 }}>
                        {b.sub}{isEmpty ? ' · none' : ` · ${count} time${count === 1 ? '' : 's'}`}
                      </span>
                    </button>
                  )
                })}
              </div>
              {/* Times in the selected window */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {visible.map((s, i) => {
                  const on = selected && selected.ref === s.ref && selected.teeTime === s.teeTime
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setSelected(s)}
                      style={{
                        padding: '10px 14px', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700,
                        border: on ? `2px solid ${primary}` : '1px solid #e2e8f0',
                        background: on ? `${primary}14` : '#fff', color: '#0f172a',
                      }}
                    >
                      {fmtTime(s.teeTime)}
                      {s.availableSlots != null && <span style={{ display: 'block', fontSize: 10, fontWeight: 500, color: '#94a3b8' }}>{s.availableSlots} open</span>}
                    </button>
                  )
                })}
              </div>
            </Section>
          )
        })()
      )}

      {selected && (
        <div style={{ marginTop: 18, padding: 16, background: '#f8fafc', border: `1px solid ${primary}`, borderRadius: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Confirm your round</div>
          <div style={{ fontSize: 14, color: '#334155' }}>
            {selCourse?.courseName} on <strong>{date}</strong> at <strong>{fmtTime(selected.teeTime)}</strong> for <strong>{golfers} golfer{golfers > 1 ? 's' : ''}</strong>
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>This uses {golfers} of your {pass.roundsRemaining} remaining round{pass.roundsRemaining !== 1 ? 's' : ''}. Already paid — nothing to pay at the course.</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <button type="button" onClick={confirmBooking} disabled={booking} style={{ padding: '11px 18px', background: primary, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: booking ? 'not-allowed' : 'pointer' }}>
              {booking ? 'Booking…' : 'Confirm booking'}
            </button>
            <button type="button" onClick={() => setSelected(null)} disabled={booking} style={smallSecondaryBtn}>Pick another time</button>
          </div>
        </div>
      )}
    </Shell>
  )
}

// Gift claim: a recipient opens the emailed link, sees the gifted rounds, and
// claims them — which binds the pass to their (new or existing) portal account,
// signs them in, and drops them in their wallet.
export function CustomerGiftClaim() {
  const { token } = useParams()
  const [gift, setGift] = useState(null)
  const [err, setErr] = useState(null)
  const [claiming, setClaiming] = useState(false)
  const [needPassword, setNeedPassword] = useState(false)
  const [passId, setPassId] = useState(null)
  const [pwd, setPwd] = useState('')
  const [pwd2, setPwd2] = useState('')
  const navigate = useNavigate()
  const scopedPath = useScopedPath()
  const brand = useResellerBrand()
  const primary = brand?.primaryColor || '#0f172a'

  useEffect(() => {
    API.get('/claim/' + token).then(r => {
      if (r.status >= 400) { setErr(r.data?.message || 'This gift link is invalid.'); return }
      setGift(r.data.gift)
    }) // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  async function claim() {
    setClaiming(true); setErr(null)
    const r = await API.post('/claim/' + token, {})
    setClaiming(false)
    if (r.status >= 400) { setErr(r.data?.message || 'Could not claim this gift.'); return }
    localStorage.setItem('rcp_session', r.data.sessionToken)
    setPassId(r.data.passId)
    if (r.data.needPassword) { setNeedPassword(true); return }
    navigate(scopedPath('passes/' + r.data.passId))
  }

  async function savePwd(e) {
    e.preventDefault()
    if (pwd !== pwd2) return setErr('Passwords don\'t match')
    if (pwd.length < 8) return setErr('Password must be at least 8 characters')
    setClaiming(true); setErr(null)
    const r = await API.post('/sessions/set-password', { sessionToken: localStorage.getItem('rcp_session'), password: pwd })
    setClaiming(false)
    if (r.status >= 400) return setErr(r.data?.message)
    navigate(scopedPath('passes/' + passId))
  }

  if (err && !gift) return <Shell><Box>{err}</Box></Shell>
  if (!gift) return <Shell><Box>Loading your gift…</Box></Shell>

  if (needPassword) {
    return (
      <Shell>
        <h1 style={{ fontSize: 22, marginTop: 0 }}>Set a password</h1>
        <p style={{ color: '#64748b', fontSize: 14 }}>One-time setup so you can come back to your rounds anytime.</p>
        <form onSubmit={savePwd} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input type="password" placeholder="Password (8+ chars)" value={pwd} onChange={e => setPwd(e.target.value)} style={inp} />
          <input type="password" placeholder="Confirm password" value={pwd2} onChange={e => setPwd2(e.target.value)} style={inp} />
          {err && <div style={errBox}>{err}</div>}
          <button type="submit" disabled={claiming} style={{ ...primaryBtn, background: primary }}>{claiming ? 'Saving…' : 'Save and see my rounds'}</button>
        </form>
      </Shell>
    )
  }

  const heroImg = resolveAssetUrl(gift.heroImage)
  return (
    <Shell>
      {/* Gift hero */}
      <div style={{
        position: 'relative', borderRadius: 16, overflow: 'hidden', minHeight: 160,
        background: heroImg ? `url('${heroImg}') center/cover no-repeat` : `linear-gradient(135deg, ${primary} 0%, ${primary}cc 100%)`,
      }}>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.2) 60%)' }} />
        <div style={{ position: 'absolute', left: 18, right: 18, bottom: 16, color: '#fff' }}>
          <div style={{ fontSize: 13, fontWeight: 600, opacity: 0.9, textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>🎁 A gift from {gift.buyerName}</div>
          <div style={{ fontSize: 25, fontWeight: 800, lineHeight: 1.15, marginTop: 3, textShadow: '0 1px 6px rgba(0,0,0,0.6)' }}>{gift.bundleName}</div>
        </div>
      </div>

      <div style={{ marginTop: 16, textAlign: 'center' }}>
        <div style={{ fontSize: 34, fontWeight: 800, color: primary }}>{gift.roundsTotal}</div>
        <div style={{ color: '#64748b', fontSize: 14, marginTop: -2 }}>prepaid round{gift.roundsTotal !== 1 ? 's' : ''} with {gift.reseller.name}</div>
      </div>

      {gift.message && (
        <div style={{ margin: '16px 0', padding: 14, background: '#f8fafc', borderLeft: `4px solid ${primary}`, borderRadius: 8, color: '#334155', fontSize: 14, fontStyle: 'italic' }}>
          “{gift.message}”
        </div>
      )}

      {gift.courses.length > 0 && (
        <Section title="Where you can play">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {gift.courses.map(c => <MetaChip key={c.coursesId} icon="⛳">{c.courseName}</MetaChip>)}
          </div>
        </Section>
      )}

      {err && <div style={{ ...errBox, marginTop: 14 }}>{err}</div>}

      <div style={{ marginTop: 20 }}>
        <button type="button" onClick={claim} disabled={claiming} style={{ width: '100%', padding: 15, background: primary, color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: claiming ? 'not-allowed' : 'pointer' }}>
          {claiming ? 'Claiming…' : gift.claimed ? 'Continue to your rounds →' : 'Claim your rounds →'}
        </button>
        <div style={{ textAlign: 'center', fontSize: 12, color: '#94a3b8', marginTop: 8 }}>
          We'll add these rounds to your account ({gift.recipientEmail}) so you can book tee times anytime.
        </div>
      </div>
    </Shell>
  )
}

// Split-pay: a golfer follows the "Pay your share" invite email (?b=&t=),
// sees the trip + their share, and pays their portion. Token-authed, no login.
// Reuses the embed submitPayment endpoint (paymentToken branch).
export function CustomerPayShare() {
  const [params] = useSearchParams()
  const bookingId = params.get('b')
  const token = params.get('t')
  const [ps, setPs] = useState(null)
  const [err, setErr] = useState(null)
  const [cardForm, setCardForm] = useState({ number: '', expiry: '', cvc: '', name: '' })
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const brand = useResellerBrand()
  const primary = brand?.primaryColor || '#0f172a'

  useEffect(() => {
    if (!bookingId || !token) { setErr('This payment link is missing information.'); return }
    API.get(`/pay-share/${bookingId}?token=${encodeURIComponent(token)}`).then(r => {
      if (r.status >= 400) { setErr(r.data?.message || 'Invalid link'); return }
      setPs(r.data.payShare)
    }) // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, token])

  function setNum(v) { const d = v.replace(/\D/g, '').slice(0, 19); setCardForm(c => ({ ...c, number: d.replace(/(.{4})/g, '$1 ').trim() })) }
  function setExp(v) { const d = v.replace(/\D/g, '').slice(0, 4); setCardForm(c => ({ ...c, expiry: d.length >= 3 ? d.slice(0, 2) + ' / ' + d.slice(2) : d })) }
  function setCvc(v) { setCardForm(c => ({ ...c, cvc: v.replace(/\D/g, '').slice(0, 4) })) }
  const ready = cardForm.number.replace(/\s/g, '').length >= 13 && cardForm.expiry.replace(/\D/g, '').length === 4 && cardForm.cvc.length >= 3 && cardForm.name.trim().length >= 2

  async function submit(e) {
    e.preventDefault()
    if (!ready || !ps) return
    setSubmitting(true); setErr(null)
    try {
      const url = `${_apiOrigin}/api/public/reseller-embed/${encodeURIComponent(ps.iframeKey)}/bookings/${ps.bookingId}/payments`
      const r = await axios.post(url, {
        paymentToken: token,
        card: { number: cardForm.number, expiry: cardForm.expiry, cvc: cardForm.cvc, name: cardForm.name },
        amount: ps.payer.amount,
      }, { validateStatus: () => true })
      if (r.status >= 400) { setErr(r.data?.message || 'Card declined. Please check the details and try again.'); setSubmitting(false); return }
      setDone(true)
    } catch (e2) { setErr(e2.message || 'Payment failed'); setSubmitting(false) }
  }

  if (err && !ps) return <Shell><Box>{err}</Box></Shell>
  if (!ps) return <Shell><Box>Loading…</Box></Shell>

  if (done || ps.payer.alreadyPaid) {
    return (
      <Shell>
        <div style={{ ...card, borderColor: '#a7f3d0', background: '#ecfdf5', textAlign: 'center', padding: 28 }}>
          <div style={{ fontSize: 40, marginBottom: 8 }} aria-hidden>✅</div>
          <h1 style={{ fontSize: 22, margin: '0 0 6px', color: '#065f46' }}>Your share is paid</h1>
          <p style={{ color: '#047857', fontSize: 14, margin: 0 }}>
            Thanks{ps.payer.name ? ', ' + String(ps.payer.name).split(' ')[0] : ''}! We've authorized {fmtMoney(ps.payer.amount, ps.payer.currency)}. Once everyone has paid, {ps.reseller.name} locks in the hotel and tee times and emails your confirmation.
          </p>
        </div>
      </Shell>
    )
  }

  if (ps.gateway === 'stripe') {
    return (
      <Shell>
        <div style={card}>
          <h1 style={{ fontSize: 20, marginTop: 0 }}>Pay your share</h1>
          <p style={{ color: '#475569', fontSize: 14 }}>This trip uses a secure card form we can't show here yet. Please contact {ps.reseller.name} to pay your share.</p>
        </div>
      </Shell>
    )
  }

  return (
    <Shell>
      <h1 style={{ fontSize: 22, marginTop: 0, marginBottom: 4 }}>Pay your share</h1>
      <div style={{ color: '#64748b', fontSize: 13, marginBottom: 16 }}>{ps.leaderName || 'A friend'} invited you to split <strong>{ps.packageName}</strong> · via {ps.reseller.name}</div>

      {/* Trip summary */}
      <div style={{ padding: '14px 16px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 12, marginBottom: 16, fontSize: 13, color: '#475569' }}>
        <div>📅 {String(ps.checkIn).slice(0, 10)} → {String(ps.checkOut).slice(0, 10)}</div>
        {ps.hotel && <div style={{ marginTop: 4 }}>🏨 {ps.hotel}</div>}
        {ps.rounds.map((r, i) => <div key={i} style={{ marginTop: 4 }}>⛳ {r.courseName}{r.playDate ? ' — ' + String(r.playDate).slice(0, 10) : ''}</div>)}
      </div>

      {/* Amount */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 16px', background: '#fff', border: `1px solid ${primary}`, borderRadius: 12, marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: '#475569' }}>Your share</div>
        <div style={{ fontSize: 24, fontWeight: 800, color: primary }}>{fmtMoney(ps.payer.amount, ps.payer.currency)}</div>
      </div>

      <p style={{ color: '#64748b', fontSize: 13, marginTop: 0, marginBottom: 16 }}>
        Your card is <strong>authorized now, not charged</strong> — the trip is only booked once everyone in the group has paid. Rates are held for a limited time, so pay soon.
      </p>

      {err && <div style={{ ...errBox, marginBottom: 14 }}>{err}</div>}

      <form onSubmit={submit}>
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 18 }}>
          <label style={payLabel}>Cardholder name</label>
          <input type="text" required autoComplete="cc-name" value={cardForm.name} onChange={e => setCardForm(c => ({ ...c, name: e.target.value }))} style={inp} />
          <label style={payLabel}>Card number</label>
          <input type="text" required inputMode="numeric" autoComplete="cc-number" placeholder="1234 5678 9012 3456" value={cardForm.number} onChange={e => setNum(e.target.value)} style={{ ...inp, fontFamily: 'monospace', letterSpacing: 1 }} />
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={payLabel}>Expiry (MM/YY)</label>
              <input type="text" required inputMode="numeric" autoComplete="cc-exp" placeholder="08 / 28" value={cardForm.expiry} onChange={e => setExp(e.target.value)} style={{ ...inp, fontFamily: 'monospace' }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={payLabel}>CVC</label>
              <input type="text" required inputMode="numeric" autoComplete="cc-csc" placeholder="123" value={cardForm.cvc} onChange={e => setCvc(e.target.value)} style={{ ...inp, fontFamily: 'monospace' }} />
            </div>
          </div>
        </div>
        <button type="submit" disabled={submitting || !ready} style={{ width: '100%', padding: 16, marginTop: 16, background: submitting || !ready ? '#cbd5e1' : primary, color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: submitting || !ready ? 'not-allowed' : 'pointer' }}>
          {submitting ? 'Authorizing…' : `Authorize ${fmtMoney(ps.payer.amount, ps.payer.currency)}`}
        </button>
        <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', marginTop: 10 }}>Secured by InteGolf. Card details are sent over HTTPS and processed through Moneris — never stored.</div>
      </form>
    </Shell>
  )
}

export function CustomerReviewForm() {
  const { id } = useParams()
  const [data, setData] = useState(null)
  const [rating, setRating] = useState(0)
  const [reviewText, setReviewText] = useState('')
  const [isPublic, setIsPublic] = useState(true)
  const [err, setErr] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const navigate = useNavigate()
  const scopedPath = useScopedPath()

  useEffect(() => {
    API.get('/trips/' + id + '/review', { headers: authHeaders() }).then(r => {
      if (r.status === 401) { navigate(scopedPath('login')); return }
      if (r.status >= 400) { setErr(r.data?.message); return }
      setData(r.data)
      if (r.data.review) {
        setRating(r.data.review.rating)
        setReviewText(r.data.review.reviewText || '')
        setIsPublic(r.data.review.isPublic)
      }
    })
  }, [id, navigate])

  async function submit(e) {
    e.preventDefault()
    if (rating < 1 || rating > 5) { setErr('Pick a star rating'); return }
    setSubmitting(true); setErr(null)
    const r = await API.post('/trips/' + id + '/review', { rating, reviewText, isPublic }, { headers: authHeaders() })
    setSubmitting(false)
    if (r.status >= 400) return setErr(r.data?.message || 'Could not submit')
    setSubmitted(true)
  }

  if (err && !data) return <Shell><Box>{err}<br/><Link to={scopedPath('trips/' + id)}>← Back to trip</Link></Box></Shell>
  if (!data) return <Shell><Box>Loading...</Box></Shell>

  return (
    <Shell>
      <Link to={scopedPath('trips/' + id)} style={{ color: '#64748b', fontSize: 13, textDecoration: 'none' }}>← Back to trip</Link>
      <h1 style={{ fontSize: 24, margin: '8px 0 16px' }}>{data.review ? 'Update your review' : 'How was your trip?'}</h1>

      {submitted ? (
        <div style={{ background: '#d1fae5', border: '1px solid #6ee7b7', color: '#065f46', borderRadius: 8, padding: 14 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Thanks for the review.</div>
          <div style={{ fontSize: 13 }}>It'll show up publicly once the reseller has approved it.</div>
          <div style={{ marginTop: 10 }}>
            <Link to={scopedPath('trips/' + id)} style={{ color: '#065f46', fontWeight: 600 }}>← Back to trip</Link>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 6 }}>Your rating</label>
            <div style={{ display: 'flex', gap: 4 }}>
              {[1,2,3,4,5].map(n => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 32, lineHeight: 1, color: n <= rating ? '#f59e0b' : '#e2e8f0' }}
                  aria-label={n + ' star' + (n > 1 ? 's' : '')}
                >★</button>
              ))}
            </div>
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 6 }}>Tell others about your trip (optional)</label>
            <textarea
              value={reviewText}
              onChange={e => setReviewText(e.target.value)}
              maxLength={4000}
              rows={6}
              placeholder="What did you like? What could have been better?"
              style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }}
            />
            <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'right', marginTop: 2 }}>{reviewText.length} / 4000</div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#475569' }}>
            <input type="checkbox" checked={isPublic} onChange={e => setIsPublic(e.target.checked)} />
            Allow this review to be shown publicly (after moderation)
          </label>
          {err && <div style={errBox}>{err}</div>}
          <div>
            <button type="submit" disabled={submitting} style={primaryBtn}>
              {submitting ? 'Submitting...' : (data.review ? 'Update review' : 'Submit review')}
            </button>
          </div>
        </form>
      )}
    </Shell>
  )
}

// ─── Bits + styles ──────────────────────────────────────────────────────

export function Shell({ children, focused }) {
  const brand = useResellerBrand()
  const headerBg = brand?.primaryColor || '#0f172a'
  const social = brand?.social || {}
  const socialEntries = []
  if (social.facebook) socialEntries.push({ label: 'Facebook', url: social.facebook })
  if (social.instagram) socialEntries.push({ label: 'Instagram', url: social.instagram.startsWith('http') ? social.instagram : 'https://instagram.com/' + social.instagram })
  if (social.twitter) socialEntries.push({ label: 'X', url: social.twitter.startsWith('http') ? social.twitter : 'https://x.com/' + social.twitter })

  return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6', display: 'flex', flexDirection: 'column' }}>
      <div style={{ background: headerBg, padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <div style={{ maxWidth: 720, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 14 }}>
          {brand?.logoUrl && (
            <img src={brand.logoUrl} alt={brand?.name || ''} style={{ display: 'block', maxHeight: 44, maxWidth: 160 }} />
          )}
          <div style={{ color: '#fff', fontSize: 20, fontWeight: 700, lineHeight: 1.2 }}>
            {brand?.name || 'Customer portal'}
          </div>
          <div style={{ marginLeft: 'auto' }}>
            {/* Focused mode (no-login "Book Your Rounds"): hide the account
                menu so the buyer can ONLY book this pass, nothing else. */}
            {!focused && <AccountMenu />}
          </div>
        </div>
      </div>
      <div style={{ flex: 1, padding: '32px 20px' }}>
        <div style={{ maxWidth: 720, margin: '0 auto', background: '#fff', borderRadius: 12, padding: 32, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          {children}
        </div>
      </div>
      <footer style={{ marginTop: 24, padding: '28px 20px 36px', textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
        {brand && (
          <div style={{ maxWidth: 720, margin: '0 auto' }}>
            {/* Reseller name as the anchor — matches the header chrome */}
            <div style={{ fontSize: 14, fontWeight: 700, color: '#334155', marginBottom: 12 }}>
              {brand.legalName || brand.name}
            </div>

            {/* Contact details — separated with proper bullets + spacing */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: '4px 14px', marginBottom: socialEntries.length > 0 ? 14 : 0 }}>
              {brand.supportEmail && (
                <a href={'mailto:' + brand.supportEmail} style={footerLink}>{brand.supportEmail}</a>
              )}
              {brand.supportEmail && brand.phone && <span style={footerSep}>•</span>}
              {brand.phone && (
                <a href={'tel:' + brand.phone.replace(/\D/g, '')} style={footerLink}>{displayPhone(brand.phone)}</a>
              )}
              {(brand.supportEmail || brand.phone) && brand.website && <span style={footerSep}>•</span>}
              {brand.website && (
                <a href={brand.website} target="_blank" rel="noreferrer" style={footerLink}>
                  {brand.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                </a>
              )}
            </div>

            {/* Social — small pill-shaped links */}
            {socialEntries.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 8 }}>
                {socialEntries.map(s => (
                  <a key={s.label} href={s.url} target="_blank" rel="noreferrer" style={{
                    display: 'inline-block', padding: '4px 10px',
                    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 999,
                    color: '#475569', fontSize: 12, fontWeight: 600,
                    textDecoration: 'none',
                  }}>{s.label}</a>
                ))}
              </div>
            )}
          </div>
        )}
        <div style={{ marginTop: 22, paddingTop: 14, borderTop: '1px solid #e2e8f0', fontSize: 11, color: '#94a3b8' }}>
          Powered by{' '}
          <a href="https://integolf.com" target="_blank" rel="noreferrer" style={{ color: '#64748b', textDecoration: 'none', fontWeight: 600 }}>InteGolf</a>
        </div>
      </footer>
    </div>
  )
}
function Box({ children }) { return <div style={{ color: '#475569', fontSize: 14 }}>{children}</div> }

// Shared footer link / separator styles (referenced by Shell).
const footerLink = { color: '#475569', textDecoration: 'none', fontSize: 13 }
const footerSep  = { color: '#cbd5e1', fontSize: 11 }


/**
 * Top-right account menu — avatar circle that opens a dropdown with
 * profile / security / preferences / sign-out links. Only renders when
 * the customer has a session (otherwise we'd be linking from the login
 * page itself).
 */
function AccountMenu() {
  const [open, setOpen] = useState(false)
  const [signedIn, setSignedIn] = useState(false)
  const scopedPath = useScopedPath()
  const navigate = useNavigate()

  useEffect(() => {
    setSignedIn(!!localStorage.getItem('rcp_session'))
  }, [])

  // Close on outside click.
  useEffect(() => {
    if (!open) return
    function onDoc(e) {
      if (!e.target.closest?.('[data-account-menu]')) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  async function signOut() {
    try { await API.post('/sessions/logout', {}, { headers: authHeaders() }) } catch (_) {}
    localStorage.removeItem('rcp_session')
    localStorage.removeItem('rcp_device')
    setSignedIn(false)
    setOpen(false)
    navigate(scopedPath('login'))
  }

  if (!signedIn) return null

  return (
    <div data-account-menu style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-label="Account menu"
        style={{
          width: 36, height: 36, borderRadius: '50%',
          background: 'rgba(255,255,255,0.22)', border: '1px solid rgba(255,255,255,0.35)',
          color: '#fff', fontSize: 16, fontWeight: 700,
          cursor: 'pointer', padding: 0, lineHeight: 1,
        }}
      >☰</button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0,
          minWidth: 200, background: '#fff', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)', border: '1px solid #e2e8f0',
          overflow: 'hidden', zIndex: 50,
        }}>
          <MenuLink to={scopedPath('account/profile')}     onClick={() => setOpen(false)}>Profile</MenuLink>
          <MenuLink to={scopedPath('account/security')}    onClick={() => setOpen(false)}>Security</MenuLink>
          <MenuLink to={scopedPath('account/preferences')} onClick={() => setOpen(false)}>Email preferences</MenuLink>
          <MenuLink to={scopedPath('account/danger')}      onClick={() => setOpen(false)}>Export &amp; delete account</MenuLink>
          <div style={{ borderTop: '1px solid #f1f5f9' }} />
          <button onClick={signOut} style={{
            display: 'block', width: '100%', textAlign: 'left',
            padding: '10px 14px', background: 'none', border: 'none',
            color: '#dc2626', fontSize: 14, fontWeight: 600, cursor: 'pointer',
          }}>Sign out</button>
        </div>
      )}
    </div>
  )
}
function MenuLink({ to, children, onClick }) {
  return (
    <Link to={to} onClick={onClick} style={{
      display: 'block', padding: '10px 14px',
      color: '#334155', fontSize: 14, textDecoration: 'none',
    }}>{children}</Link>
  )
}
function Section({ title, children }) {
  return (
    <div style={{ marginTop: 24 }}>
      <h2 style={{ fontSize: 13, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>{title}</h2>
      {children}
    </div>
  )
}

const inp = { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }
const primaryBtn = { padding: '12px 16px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
const smallPrimaryBtn = { padding: '7px 12px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }
const smallSecondaryBtn = { padding: '7px 12px', background: '#fff', color: '#334155', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }
const cancelBtn = { padding: '10px 14px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
const linkBtn = { background: 'none', border: 'none', padding: 0, color: '#0f172a', fontSize: 13, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }
const errBox = { padding: 10, background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, fontSize: 13 }
const card = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 14, fontSize: 14 }
// Bolder component card with a brand-coloured left accent (hotel / rounds).
const itemCard = (primary) => ({ background: '#fff', border: '1px solid #e2e8f0', borderLeft: `4px solid ${primary}`, borderRadius: 10, padding: 14, fontSize: 14 })
const itemIcon = (primary) => ({ flexShrink: 0, width: 38, height: 38, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 19, background: `${primary}14` })

function fmtMoney(value, currency) {
  const n = Number(value)
  if (!Number.isFinite(n)) return ''
  const formatted = '$' + n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return currency ? `${currency} ${formatted}` : formatted
}

function statusPill(bucket) {
  const colors = {
    pending:     { bg: '#fef3c7', fg: '#92400e' },
    in_progress: { bg: '#dbeafe', fg: '#1e40af' },
    completed:   { bg: '#d1fae5', fg: '#065f46' },
  }[bucket] || { bg: '#e2e8f0', fg: '#475569' }
  return { display: 'inline-block', padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: colors.bg, color: colors.fg, textTransform: 'uppercase', letterSpacing: 0.5 }
}

// Small pill used in the redesigned trip cards (📅 dates, 🌙 nights, ⛳ rounds…)
function MetaChip({ icon, children }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#475569', background: '#f1f5f9', borderRadius: 999, padding: '3px 9px' }}>
      <span aria-hidden>{icon}</span>{children}
    </span>
  )
}

// Translucent pill for use over the dark trip-detail hero image.
function HeroChip({ icon, children }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, fontWeight: 600, color: '#fff', background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.28)', backdropFilter: 'blur(2px)', borderRadius: 999, padding: '4px 11px' }}>
      <span aria-hidden>{icon}</span>{children}
    </span>
  )
}
function AltCoursePickerModal({ round, alternateCourses, submitting, onCancel, onSubmit }) {
  // Defaults mirror the round's current selection — customer overrides
  // only what they want to change.
  const [coursesId, setCoursesId] = useState(round?.Courses_ID || '')
  const [playDate, setPlayDate] = useState(round?.PlayDate ? String(round.PlayDate).slice(0, 10) : '')
  const [teeTime, setTeeTime] = useState(round?.TeeTime ? String(round.TeeTime).slice(0, 5) : '')
  const [err, setErr] = useState(null)

  function submit(e) {
    e.preventDefault()
    setErr(null)
    // At least ONE field must differ from the current round — empty
    // body would just trigger best-available retry.
    const changedCourse = String(coursesId) !== String(round?.Courses_ID || '')
    const changedDate = playDate && playDate !== (round?.PlayDate ? String(round.PlayDate).slice(0, 10) : '')
    const changedTime = teeTime && teeTime !== (round?.TeeTime ? String(round.TeeTime).slice(0, 5) : '')
    if (!changedCourse && !changedDate && !changedTime) {
      setErr('Change at least one field, or close this and use "Try an alternate" with the default suggestion.')
      return
    }
    onSubmit({
      altCoursesId: changedCourse ? Number(coursesId) : null,
      altPlayDate:  changedDate ? playDate : null,
      altTeeTime:   changedTime ? teeTime + ':00' : null,
    })
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16, zIndex: 100,
    }}>
      <form onSubmit={submit} style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 480, width: '100%' }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 20 }}>Pick an alternate</h2>
        <div style={{ color: '#64748b', fontSize: 13, marginBottom: 16 }}>
          Currently: <strong>{round?.CourseName}</strong> on {String(round?.PlayDate).slice(0, 10)} at {String(round?.TeeTime || '').slice(0, 5)}
        </div>

        <label style={{ fontSize: 13, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 4 }}>Course</label>
        <select value={coursesId} onChange={e => setCoursesId(e.target.value)} style={{ ...inp, padding: '8px 10px', marginBottom: 12 }}>
          <option value={round?.Courses_ID || ''}>{round?.CourseName} (current)</option>
          {alternateCourses.map(c => (
            <option key={c.coursesId} value={c.coursesId}>{c.courseName}{c.city ? ` — ${c.city}` : ''}</option>
          ))}
        </select>

        <label style={{ fontSize: 13, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 4 }}>Play date</label>
        <input type="date" value={playDate} onChange={e => setPlayDate(e.target.value)} style={{ ...inp, padding: '8px 10px', marginBottom: 12 }} />

        <label style={{ fontSize: 13, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 4 }}>Tee time</label>
        <input type="time" value={teeTime} onChange={e => setTeeTime(e.target.value)} style={{ ...inp, padding: '8px 10px', marginBottom: 12 }} />

        <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>
          We'll attempt the new pick. If it's not available, we'll surface another action item with what we can offer.
        </div>

        {err && <div style={{ ...errBox, marginBottom: 10 }}>{err}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onCancel} disabled={submitting} style={smallSecondaryBtn}>Cancel</button>
          <button type="submit" disabled={submitting} style={smallPrimaryBtn}>
            {submitting ? 'Submitting...' : 'Submit alternate'}
          </button>
        </div>
      </form>
    </div>
  )
}

function actionLabel(action) {
  return {
    keep: 'Keep with note',
    change_datetime: 'Change date / time',
    cancel: 'Cancel this item',
    other: 'Other',
  }[action] || action
}
function requestStatusPill(status) {
  const map = {
    open:        { bg: '#fef3c7', fg: '#92400e' },
    in_progress: { bg: '#dbeafe', fg: '#1e40af' },
    resolved:    { bg: '#d1fae5', fg: '#065f46' },
    declined:    { bg: '#fee2e2', fg: '#991b1b' },
  }
  const c = map[status] || { bg: '#e2e8f0', fg: '#475569' }
  return { display: 'inline-block', padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: c.bg, color: c.fg, textTransform: 'uppercase', letterSpacing: 0.5 }
}
function paymentPill(status) {
  const map = {
    pending:    { bg: '#fef3c7', fg: '#92400e' },
    authorized: { bg: '#dbeafe', fg: '#1e40af' },
    captured:   { bg: '#d1fae5', fg: '#065f46' },
    refunded:   { bg: '#e2e8f0', fg: '#475569' },
    voided:     { bg: '#e2e8f0', fg: '#475569' },
    declined:   { bg: '#fee2e2', fg: '#991b1b' },
  }
  const c = map[status] || { bg: '#e2e8f0', fg: '#475569' }
  return { display: 'inline-block', padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: c.bg, color: c.fg, textTransform: 'uppercase', letterSpacing: 0.5 }
}
function componentPill(status) {
  const map = {
    confirmed:               { bg: '#d1fae5', fg: '#065f46' },
    pending:                 { bg: '#fef3c7', fg: '#92400e' },
    failed_pending_action:   { bg: '#fee2e2', fg: '#991b1b' },
    cancelled:               { bg: '#e2e8f0', fg: '#475569' },
    refunded:                { bg: '#e2e8f0', fg: '#475569' },
  }
  const c = map[status] || { bg: '#e2e8f0', fg: '#475569' }
  return { display: 'inline-block', padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: c.bg, color: c.fg }
}
